import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import {
  Share2, ChevronDown, ChevronLeft, ChevronRight, Check, X, FileText,
  Users, Phone, MessageCircle, Download, Play, Images
} from 'lucide-react';
import { packageDayRangeCount } from '../utils/packageDayFields.js';
import {
  normalizeEnabledScheduleIds,
  resolveJoinPackageDays
} from '../utils/joinPackageDuration.js';
import { buildItineraryDisplayDayNumbers, itineraryDateOffsetFromDisplayDay } from '../utils/itineraryDayNumber.js';
import { formatStayOptionLabel } from '../utils/formatStayOptionLabel.js';
import { filterPackagesForLocalCamps, isLocalCampTrek } from '../utils/localCampPackages.js';
import {
  optimizeImageUrl,
  imageLoadErrorFallback,
  IMAGE_DISPLAY_WIDTH
} from '../utils/optimizeImageUrl.js';

const FALLBACK_EVENT_IMAGE = 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200';

function trekDisplayImageUrl(url, width = IMAGE_DISPLAY_WIDTH.card) {
  return optimizeImageUrl(url, { width });
}

function trekImageOnError(e, fallbackSrc) {
  const next = imageLoadErrorFallback(e.currentTarget.src, fallbackSrc, FALLBACK_EVENT_IMAGE);
  if (next === e.currentTarget.src) {
    e.currentTarget.onerror = null;
    return;
  }
  e.currentTarget.src = next;
}

/** Dynamic quick policy links + legacy trek columns when JSON row has title only. */
function parseQuickPolicyLinksFromTrek(trek) {
  if (!trek) return [];
  let raw = trek.quickPolicyLinks ?? trek.quick_policy_links;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      raw = [];
    }
  }
  if (!Array.isArray(raw)) raw = [];

  function entryBody(x) {
    if (x == null) return '';
    const v = x.content ?? x.body ?? x.html ?? x.text ?? x.details;
    if (v == null) return '';
    if (typeof v === 'string') return v;
    if (typeof v === 'object' && typeof v.html === 'string') return v.html;
    return String(v);
  }

  function legacyForTitle(title) {
    const t = String(title || '').trim().toLowerCase();
    if (t.includes('cancellation')) return String(trek.cancellationPolicy || trek.cancellation_policy || '').trim();
    if (t.includes('terms') && t.includes('condition')) {
      return String(trek.termsAndConditions || trek.terms_and_conditions || '').trim();
    }
    if (t.includes('things') && (t.includes('carri') || t.includes('carry'))) {
      return String(trek.thingsToCarry || trek.things_to_carry || '').trim();
    }
    return '';
  }

  const fromJson = raw
    .filter((x) => x && (String(x.title || '').trim() || String(entryBody(x) || '').trim()))
    .map((x) => {
      const title = String(x.title || '').trim() || 'Policy';
      let content = String(entryBody(x) || '').trim();
      if (!content) content = legacyForTitle(title);
      return {
        icon: String(x.icon || 'file-text').trim() || 'file-text',
        title,
        content
      };
    })
    .filter((x) => x.title || x.content);
  if (fromJson.length > 0) return fromJson;
  const legacy = [];
  if (String(trek.thingsToCarry || '').trim()) {
    legacy.push({ icon: 'briefcase', title: 'Things To Be Carried', content: trek.thingsToCarry });
  }
  if (String(trek.cancellationPolicy || '').trim()) {
    legacy.push({ icon: 'clock', title: 'Cancellation Policy', content: trek.cancellationPolicy });
  }
  if (String(trek.termsAndConditions || '').trim()) {
    legacy.push({ icon: 'shield', title: 'Terms & Conditions', content: trek.termsAndConditions });
  }
  return legacy;
}
import { getIncludeIconDisplay, getIncludeSidebarLucideComponent } from '../utils/feeIncludeIcons';
import { useEvents, useSiteSettings } from '../hooks/useContent';
import { usePageSeo } from '../hooks/usePageSeo';
import { buildTrekPageSeo, buildTouristTripSchema, buildBreadcrumbSchema, buildFaqSchema } from '../utils/seo';
import { useAuth } from '../context/AuthContext';
import { dynamicAPI, contentAPI } from '../services/api';
import { downloadBrochureFromUrl, openBrochurePlaceholderWindow, resolveBrochureCdnUrl } from '../utils/downloadBrochureFromUrl.js';
import EventCard from '../components/EventCard';
import Breadcrumbs from '../components/Breadcrumbs';
import { pushDocumentScrollLock, popDocumentScrollLock } from '../utils/documentScrollLock.js';
import RichContent from '../components/RichContent';

import LoadingIndicator from '../components/LoadingIndicator';
import EventShareModal from '../components/EventShareModal';
import { normalizeTrekDifficultyLabel } from '../constants/trekDifficulty.js';
import { resolveOfficeTelHref } from '../utils/resolveOfficeTelHref.js';
import { resolveOfficeWhatsAppHref } from '../utils/resolveOfficeWhatsAppHref.js';
import { trackViewItem } from '../utils/googleAnalytics.js';
import EventWebstories from '../components/EventWebstories';
import './EventDetail.css';
import '../components/EventCard.css';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** Experience gallery: items shown before “Load more” (e.g. 2×2 on mobile). */
const EVENT_DETAIL_GALLERY_COLLAPSED_COUNT = 4;

const LOGISTICS_ICON_DEFAULTS = {
  duration: 'calendar',
  lengthKm: 'route',
  age: 'users',
  difficulty: 'mountain',
  altitude: 'mountain'
};

function parseLogisticsNumber(v) {
  if (v == null || v === '') return NaN;
  const n = Number(String(v).replace(/,/g, '').trim());
  return Number.isFinite(n) ? n : NaN;
}

/** Plain numeric altitude → "17000 ft"; strips redundant ft then re-adds with a space. */
function formatAltitudeWithFt(alt) {
  const s = String(alt ?? '').trim();
  if (!s) return '';
  const core = s.replace(/\s*(?:ft|feet)\s*$/i, '').trim();
  const normalized = core.replace(/,/g, '');
  if (!/^\d+(\.\d+)?$/.test(normalized)) return s;
  return `${core} ft`;
}

/** Experience Gallery video: optional admin poster (thumbnailUrl), else YouTube default. */
function experienceGalleryVideoPosterSrc(item) {
  const custom = String(item?.thumbnailUrl ?? item?.thumbnail_url ?? '').trim();
  if (custom) return trekDisplayImageUrl(custom, IMAGE_DISPLAY_WIDTH.gallery);
  const vid = item?.videoId;
  /* 16:9 YouTube thumbs (hqdefault is 4:3 and shows letterboxing in wide tiles). */
  return vid ? `https://img.youtube.com/vi/${vid}/mqdefault.jpg` : '';
}

/**
 * @param {object|null|undefined} trek — batch.trek or full trek
 * @param {{ durationDays?: number, events?: array, pageSlug?: string, pageEvent?: object }} opts
 */
function buildLogisticsRows(trek, opts = {}) {
  const { durationDays, events, pageSlug, pageEvent } = opts;
  const t = trek && typeof trek === 'object' ? trek : null;
  const rawIcons = t?.logisticsIcons ?? t?.logistics_icons;
  const icons = rawIcons && typeof rawIcons === 'object' ? rawIcons : {};
  const iconSlugFor = (key) =>
    String(icons[key] || '').trim() || LOGISTICS_ICON_DEFAULTS[key] || 'check';

  const eventFromList =
    Array.isArray(events) && pageSlug
      ? events.find((e) => e.slug === pageSlug || String(e.slug) === String(pageSlug))
      : null;

  const mergedDifficulty =
    (t && t.difficulty) || eventFromList?.difficulty || pageEvent?.difficulty || null;

  const rows = [];

  const dDays = parseLogisticsNumber(durationDays);
  if (Number.isFinite(dDays) && dDays > 0) {
    rows.push({
      key: 'duration',
      iconSlug: iconSlugFor('duration'),
      label: 'Duration',
      value: `${dDays} Day${dDays === 1 ? '' : 's'}`
    });
  }

  if (!t) return rows;

  const km = parseLogisticsNumber(t.lengthKm ?? t.length_km);
  if (Number.isFinite(km) && km > 0) {
    rows.push({
      key: 'length',
      iconSlug: iconSlugFor('lengthKm'),
      label: 'Distance',
      value: `${km} km`
    });
  }

  const minA = t.minAge ?? t.min_age;
  const maxA = t.maxAge ?? t.max_age;
  const minN = parseLogisticsNumber(minA);
  const maxN = parseLogisticsNumber(maxA);
  const hasMin = Number.isFinite(minN);
  const hasMax = Number.isFinite(maxN);
  if (hasMin || hasMax) {
    let value = '';
    if (hasMin && hasMax) value = `${minN}–${maxN} years`;
    else if (hasMin) value = `Min ${minN} years`;
    else value = `Up to ${maxN} years`;
    rows.push({ key: 'age', iconSlug: iconSlugFor('age'), label: 'Age', value });
  }

  if (mergedDifficulty) {
    rows.push({
      key: 'difficulty',
      iconSlug: iconSlugFor('difficulty'),
      label: 'Difficulty',
      value: normalizeTrekDifficultyLabel(mergedDifficulty)
    });
  }

  const alt = t.altitude;
  if (alt != null && String(alt).trim()) {
    rows.push({
      key: 'altitude',
      iconSlug: iconSlugFor('altitude'),
      label: 'Altitude',
      value: formatAltitudeWithFt(alt)
    });
  }

  return rows;
}
const MOBILE_DIGITS = 10;

const normalizeMobile = (value) => String(value || '').replace(/\D/g, '').slice(0, MOBILE_DIGITS);
const isValidMobile = (value) => {
  const digits = normalizeMobile(value);
  return digits.length === MOBILE_DIGITS && /^[6-9][0-9]{9}$/.test(digits);
};
const inferTravelPackageFromText = (value) => {
  const text = String(value || '').toLowerCase();
  if (!text) return null;
  if (text.includes('flight') || text.includes('air')) return 'flight';
  if (text.includes('train') || text.includes('sleeper') || text.includes('tier') || text.includes('coach')) return 'train';
  if (text.includes('bus') || text.includes('volvo')) return 'bus';
  return null;
};
const toDateKey = (value) => String(value || '').split('T')[0];
const parseDateParts = (value) => {
  const key = toDateKey(value);
  const parts = key.split('-');
  if (parts.length !== 3) return null;
  const [y, m, d] = parts.map((n) => Number(n));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  return { year: y, month: m, day: d };
};
const getMonthIndexFromDate = (value) => {
  const parts = parseDateParts(value);
  return parts ? parts.month - 1 : null;
};
/** Chronological month bucket, e.g. `2027-01` (not just month index). */
const getYearMonthKeyFromDate = (value) => {
  const parts = parseDateParts(value);
  if (!parts) return null;
  return `${parts.year}-${String(parts.month).padStart(2, '0')}`;
};
/** Month pill label: chronological order; year suffix when batches span calendar years. */
function departureMonthLabel(yearMonthKey, allYearMonthKeys, monthNames) {
  const [year, mm] = yearMonthKey.split('-');
  const monthIdx = Number(mm) - 1;
  const name = monthNames[monthIdx] || '';
  const years = new Set(allYearMonthKeys.map((k) => k.split('-')[0]));
  if (years.size <= 1) return name;
  if (allYearMonthKeys.filter((k) => k.split('-')[1] === mm).length > 1) {
    return `${name} ${year}`;
  }
  const firstYear = allYearMonthKeys[0]?.split('-')[0];
  if (year !== firstYear) return `${name} ${year}`;
  return name;
}

const getDayOfMonthFromDate = (value) => {
  const parts = parseDateParts(value);
  return parts ? parts.day : null;
};

/** Local calendar date key (YYYY-MM-DD) + whole days — matches itinerary vs departure pills (avoids ISO date TZ issues). */
function addDaysToDateKey(dateKeyOrIso, dayDelta) {
  const parts = parseDateParts(dateKeyOrIso);
  if (!parts || !Number.isFinite(dayDelta)) return null;
  const d = new Date(parts.year, parts.month - 1, parts.day);
  d.setDate(d.getDate() + dayDelta);
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${da}`;
}

/** Shown in the booking sidebar when a trek has no Inclusions rows in admin yet */
const DEFAULT_BOOKING_FEE_INCLUDES = [
  { name: 'Food', icon: 'utensils' },
  { name: 'Travelling', icon: 'briefcase' },
  { name: 'Accommodation', icon: 'building' },
  { name: 'First Aid', icon: 'plus' },
  { name: 'GST', icon: 'file-text' },
  { name: 'Instructors', icon: 'users' }
];

/** Format duration for Similar Adventures card: show "X Days" (or "X Days / Y Nights") instead of date range. */
function formatSimilarEventDuration(event) {
  const days = event.durationDays != null ? Number(event.durationDays) : null;
  if (days != null && days > 0) {
    const nights = days - 1;
    return nights > 0 ? `${days} Days / ${nights} Night${nights > 1 ? 's' : ''}` : `${days} Day${days > 1 ? 's' : ''}`;
  }
  const range = event.duration;
  if (typeof range === 'string' && range.includes(' - ')) {
    const [startStr, endStr] = range.split(' - ').map((s) => s.trim());
    const start = new Date(startStr);
    const end = new Date(endStr);
    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
      const diff = Math.round((end - start) / (24 * 60 * 60 * 1000)) + 1;
      if (diff > 0) {
        return `${diff} Day${diff !== 1 ? 's' : ''}`;
      }
    }
  }
  return 'TBA';
}

/** Lowercased blob of itinerary fields for join-city heuristics (merged multi-city schedules). */
function scheduleDayTextBlob(day) {
  return [day?.name, day?.brief, day?.description]
    .map((x) => (typeof x === 'string' ? x : ''))
    .join(' ')
    .toLowerCase();
}

/**
 * Drop days that mention another join-from city but not the selected one. Returns null if
 * the result would be empty (keep full list) or there is nothing to disambiguate.
 */
function filterScheduleDaysByJoinCity(days, activeJoinName, allJoinNames) {
  const needle = String(activeJoinName || '').trim().toLowerCase();
  if (!needle || !Array.isArray(days) || days.length === 0) return null;
  const siblingSet = new Set(
    allJoinNames
      .map((n) => String(n || '').trim().toLowerCase())
      .filter((n) => n && n !== needle)
  );
  if (siblingSet.size === 0) return null;

  const filtered = days.filter((day) => {
    const blob = scheduleDayTextBlob(day);
    if (blob.includes(needle)) return true;
    for (const s of siblingSet) {
      if (blob.includes(s)) return false;
    }
    return true;
  });
  return filtered.length > 0 ? filtered : null;
}

/** Same ordering as backend nested packages: main first, then priority, then createdAt. */
function sortTrekPackagesForDisplay(a, b) {
  const mainA = a.isMain ? 1 : 0;
  const mainB = b.isMain ? 1 : 0;
  if (mainB !== mainA) return mainB - mainA;
  const priA = Number(a.priority) || 0;
  const priB = Number(b.priority) || 0;
  if (priA !== priB) return priA - priB;
  const tA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
  const tB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
  if (tA !== tB) return tA - tB;
  return String(a.id ?? '').localeCompare(String(b.id ?? ''));
}

/**
 * Itinerary rows for one join-from package: same rules as the main Event Detail list
 * (enabled IDs → day-number range → multi-city name filter).
 */
function resolveScheduleDaysForPackage(pkg, allScheduleDays, visibleJoinPkgs) {
  const enabledIds = normalizeEnabledScheduleIds(pkg?.enabledScheduleIds);
  let scheduleDays = allScheduleDays;

  if (enabledIds.length > 0) {
    const byDayId = new Map(allScheduleDays.map((day) => [String(day.id), day]));
    const orderedByPackage = enabledIds
      .map((id) => byDayId.get(String(id)))
      .filter(Boolean);
    if (orderedByPackage.length > 0) {
      scheduleDays = orderedByPackage;
    } else {
      scheduleDays = allScheduleDays.filter((day) =>
        enabledIds.some((id) => String(id) === String(day.id))
      );
    }
  } else if (allScheduleDays.length > 0) {
    const packageStartDayRaw = pkg?.startDayOffset ?? pkg?.startDay;
    const packageEndDayRaw = pkg?.endDayOffset ?? pkg?.endDay;
    const packageStartDay =
      packageStartDayRaw != null && packageStartDayRaw !== '' ? Number(packageStartDayRaw) : null;
    const packageEndDay =
      packageEndDayRaw != null && packageEndDayRaw !== '' ? Number(packageEndDayRaw) : null;

    if (Number.isFinite(packageStartDay) && Number.isFinite(packageEndDay)) {
      const start = Math.min(packageStartDay, packageEndDay);
      const end = Math.max(packageStartDay, packageEndDay);
      const isZeroBasedRange = start <= 0;
      scheduleDays = allScheduleDays.filter((day, idx) => {
        const fallbackDayNo = isZeroBasedRange ? idx : idx + 1;
        const orderNumber = day.order != null && day.order !== '' ? Number(day.order) : null;
        const dayNo = Number.isFinite(orderNumber) ? orderNumber : fallbackDayNo;
        return dayNo >= start && dayNo <= end;
      });
      if (scheduleDays.length === 0) {
        scheduleDays = allScheduleDays;
      }
    }
  }

  const siblingList = Array.isArray(visibleJoinPkgs) ? visibleJoinPkgs : [];
  if (enabledIds.length === 0 && siblingList.length >= 2) {
    const joinName = String(pkg?.name || '').trim();
    const allJoinNames = siblingList.map((x) => String(x.name || '').trim()).filter(Boolean);
    const narrowed = filterScheduleDaysByJoinCity(scheduleDays, joinName, allJoinNames);
    if (narrowed) scheduleDays = narrowed;
  }

  return scheduleDays;
}

/** Package start_day (Hasura offsets); used only to shift the first itinerary date vs reference package — not row `order`. */
function readPackageStartDay(p) {
  if (!p || typeof p !== 'object') return 0;
  const raw = p.startDayOffset ?? p.startDay ?? p.start_day;
  if (raw == null || raw === '') return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

const FULL_DEPARTURE_TOOLTIP = 'No seats left for this departure.';
const PACKAGE_UNAVAILABLE_TOOLTIP = 'Not available for this departure date.';
const PACKAGE_FULL_TOOLTIP = 'No seats left for this travel option.';
const STAY_UNAVAILABLE_TOOLTIP = 'Not available for this departure date.';
const STAY_FULL_TOOLTIP = 'No capacity left for this stay option.';

function joinPackageAvailability(batch, pkgId) {
  const meta = batch?.availabilityMeta?.packages?.[String(pkgId)];
  if (!meta) return { disabled: false, tooltip: undefined };
  if (meta.enabled === false) return { disabled: true, tooltip: PACKAGE_UNAVAILABLE_TOOLTIP };
  if (meta.isFull) return { disabled: true, tooltip: PACKAGE_FULL_TOOLTIP };
  return { disabled: false, tooltip: undefined };
}

function stayOptionAvailability(batch, optId) {
  const meta = batch?.availabilityMeta?.stayOptions?.[String(optId)];
  if (!meta) return { disabled: false, tooltip: undefined };
  if (meta.enabled === false) return { disabled: true, tooltip: STAY_UNAVAILABLE_TOOLTIP };
  if (meta.isFull) return { disabled: true, tooltip: STAY_FULL_TOOLTIP };
  return { disabled: false, tooltip: undefined };
}

/** Batch row from getBatchesByTrekSlug (see packageStatusById). */
function departureDateDisabledForSelectedPackage(batchRow, joinPackageId) {
  if (!batchRow) return true;
  const jid = joinPackageId != null && joinPackageId !== '' ? String(joinPackageId) : '';
  if (jid && /^\d+$/.test(jid)) {
    const st = batchRow.packageStatusById?.[jid];
    if (st === 'off' || st === 'full') return true;
    if (st === 'ok') return false;
  }
  return batchRow.isFull === true;
}

function departureDateTitleForSelectedPackage(batchRow, joinPackageId) {
  if (!batchRow) return undefined;
  const jid = joinPackageId != null && joinPackageId !== '' ? String(joinPackageId) : '';
  if (jid && /^\d+$/.test(jid)) {
    const st = batchRow.packageStatusById?.[jid];
    if (st === 'off') return PACKAGE_UNAVAILABLE_TOOLTIP;
    if (st === 'full') return PACKAGE_FULL_TOOLTIP;
  }
  if (batchRow.isFull) return FULL_DEPARTURE_TOOLTIP;
  return undefined;
}

function mapApiBatchForTrekList(b) {
  const ps = b.packageStatusById;
  return {
    id: b.id,
    startDate: b.startDate,
    endDate: b.endDate,
    duration: `${b.startDate || ''} - ${b.endDate || ''}`,
    capacity: b.capacity ?? null,
    booked: b.booked ?? null,
    spotsLeft: b.spotsLeft ?? null,
    isFull: b.isFull === true,
    packageStatusById: ps && typeof ps === 'object' && !Array.isArray(ps) ? ps : null
  };
}

function pickInitialBatchIdFromList(list, dateParam, norm) {
  if (dateParam) {
    const match = list.find((x) => norm(x.startDate) === dateParam);
    if (match) return match.id;
  }
  const firstAvail = list.find((x) => !x.isFull);
  return (firstAvail ?? list[0])?.id ?? null;
}

const EventDetail = () => {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const batchId = searchParams.get('batch');
  const dateParam = searchParams.get('date'); // YYYY-MM-DD from EventCard "More Details"
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { events, loading: eventsLoading } = useEvents({ limit: 100 });
  const { settings: siteSettings } = useSiteSettings();
  const [event, setEvent] = useState(null);
  const [batch, setBatch] = useState(null);
  const [trek, setTrek] = useState(null);
  const [allBatchesForTrek, setAllBatchesForTrek] = useState([]);
  const [loading, setLoading] = useState(true);
  /** False until slug/batch resolution finishes — avoids flashing "Event Not Found" while APIs load. */
  const [eventResolveDone, setEventResolveDone] = useState(false);
  const [initialBatchLoaded, setInitialBatchLoaded] = useState(false);
  const [travelPackage, setTravelPackage] = useState('bus');
  const [stayOption, setStayOption] = useState('quad');
  const [selectedJoinFromId, setSelectedJoinFromId] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [expandedItineraryDayId, setExpandedItineraryDayId] = useState(null);
  const [aboutExpanded, setAboutExpanded] = useState(false);
  const [aboutIsTruncated, setAboutIsTruncated] = useState(false);
  const aboutTextRef = useRef(null);
  const [callbackModalOpen, setCallbackModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [callbackForm, setCallbackForm] = useState({ name: '', phone: '', email: '', reason: '', message: '' });
  const [callbackSubmitting, setCallbackSubmitting] = useState(false);
  const [callbackSuccess, setCallbackSuccess] = useState(false);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfForm, setPdfForm] = useState({ name: '', mobile: '' });
  const [pdfSubmitting, setPdfSubmitting] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const [activeVideoId, setActiveVideoId] = useState(null);
  const [heroLightboxOpen, setHeroLightboxOpen] = useState(false);
  const [heroLightboxIndex, setHeroLightboxIndex] = useState(0);
  const [heroIndex, setHeroIndex] = useState(0);
  const [policyModalIndex, setPolicyModalIndex] = useState(null);
  /** Attractions: first row = 4 cards (matches desktop grid); Load more reveals the rest */
  const [attractionsExpanded, setAttractionsExpanded] = useState(false);
  const [attractionsGridDesktop, setAttractionsGridDesktop] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches
  );
  /** Gallery: first row count follows grid columns (4 / 2 / 1) */
  const [galleryExpanded, setGalleryExpanded] = useState(false);
  const [showMobileBookBar, setShowMobileBookBar] = useState(true);
  const mobileBookButtonRef = useRef(null);
  const heroGalleryLenRef = useRef(0);

  const trekForSeo = batch?.trek || trek;
  const trekNameForSeo =
    String(trekForSeo?.name || trekForSeo?.title || trek?.name || event?.title || '').trim();
  const trekSeo = trekForSeo || trekNameForSeo
    ? buildTrekPageSeo(
        {
          ...(trekForSeo || {}),
          name: trekNameForSeo || trekForSeo?.name || trekForSeo?.title || 'Trek',
          title: trekNameForSeo || trekForSeo?.title
        },
        siteSettings
      )
    : null;
  const publicBase = String(siteSettings.site_public_url || '').replace(/\/$/, '')
    || (typeof window !== 'undefined' ? window.location.origin : '');

  const trekSchema = useMemo(() => {
    if (!trekForSeo && !trek) return null;
    const currentTrek = trekForSeo || trek;
    const trip = buildTouristTripSchema(currentTrek, allBatchesForTrek, siteSettings, publicBase);
    const breadcrumbs = buildBreadcrumbSchema([
      { name: 'Events & Treks', url: '/events' },
      { name: trekNameForSeo || currentTrek?.name || 'Trek Details', url: `/events/${slug}` }
    ], publicBase);
    
    let rawFaqs = [];
    if (currentTrek?.faqs) {
      try {
        const parsed = typeof currentTrek.faqs === 'string' ? JSON.parse(currentTrek.faqs) : currentTrek.faqs;
        if (Array.isArray(parsed) && parsed.length > 0) {
          rawFaqs = parsed.filter(f => f && (f.q || f.question || f.a || f.answer));
        }
      } catch {
        rawFaqs = [];
      }
    }
    const faqSchema = buildFaqSchema(rawFaqs.map(f => ({ question: f.q || f.question, answer: f.a || f.answer })));

    return {
      '@context': 'https://schema.org',
      '@graph': [trip, breadcrumbs, ...(faqSchema ? [faqSchema] : [])].filter(Boolean)
    };
  }, [trekForSeo, trek, allBatchesForTrek, siteSettings, publicBase, trekNameForSeo, slug]);

  usePageSeo({
    enabled: Boolean(trekSeo?.title && slug),
    title: trekSeo?.title,
    description: trekSeo?.description,
    canonical: slug ? `${publicBase}/events/${slug}` : undefined,
    image: trekForSeo?.media?.[0]?.url || trek?.imageUrl || undefined,
    schema: trekSchema
  });

  useEffect(() => {
    const lockScroll =
      pdfModalOpen || callbackModalOpen || policyModalIndex != null || heroLightboxOpen || shareModalOpen;
    if (!lockScroll) return;
    pushDocumentScrollLock();
    return () => popDocumentScrollLock();
  }, [pdfModalOpen, callbackModalOpen, policyModalIndex, heroLightboxOpen, shareModalOpen]);

  useEffect(() => {
    if (!trek?.id && !trek?.name) return;
    trackViewItem({
      id: trek.id || trek.slug,
      name: trek.name || trek.title,
      category: trek.category || 'Trek',
      price: trek.fee || trek.price
    });
  }, [trek?.id, trek?.slug]);

  useEffect(() => {
    if (!heroLightboxOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setHeroLightboxOpen(false);
      const len = heroGalleryLenRef.current;
      if (len <= 1) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setHeroLightboxIndex((i) => (i - 1 + len) % len);
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setHeroLightboxIndex((i) => (i + 1) % len);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [heroLightboxOpen]);

  useEffect(() => {
    setAttractionsExpanded(false);
    setGalleryExpanded(false);
  }, [slug, batch?.id]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = () => setAttractionsGridDesktop(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Mobile UX: hide the sticky bottom book bar when the real price card is visible.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    if (!isMobile) return;

    // Fixed bar hides whenever the original “Book Now” button from the price card
    // is in the viewport. This is more robust than IntersectionObserver in layouts
    // that use `display: contents` for reordering.
    let rafId = null;
    const BAR_HIDE_BUFFER_PX = 0;

    const update = () => {
      const btn = mobileBookButtonRef.current;
      if (!btn) {
        setShowMobileBookBar(true);
        return;
      }

      const rect = btn.getBoundingClientRect();
      const inViewport =
        rect.top < window.innerHeight - BAR_HIDE_BUFFER_PX &&
        rect.bottom > 0 + BAR_HIDE_BUFFER_PX;

      setShowMobileBookBar(!inViewport);
    };

    const onScrollOrResize = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        update();
      });
    };

    update();
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [loading, batch?.id, event?.id]);

  // Reset only when the trek slug changes — not when ?batch= changes (departure date picker).
  useEffect(() => {
    setEventResolveDone(false);
    setEvent(null);
    setBatch(null);
    setTrek(null);
    setAllBatchesForTrek([]);
    setInitialBatchLoaded(false);
    setLoading(true);
  }, [slug]);

  // Resolve event: either by batchId, or by fetching all batches by slug (so we have dates + correct ?date= from the start)
  useEffect(() => {
    let cancelled = false;
    const norm = (s) => (s || '').split('T')[0];

    const finishResolve = (resolvedEvent) => {
      if (cancelled) return;
      // Keep loading until the events list is ready if we still need it as fallback.
      if (!resolvedEvent && eventsLoading) return;
      setEventResolveDone(true);
    };

    const loadTrekOnlyPage = async (trekId, trekSlug) => {
      if (!trekId) return false;
      try {
        const trekRes = await dynamicAPI.getTrekById(trekId);
        if (cancelled) return false;
        const trekData = trekRes?.data?.data;
        if (!trekData?.id) return false;
        setTrek(trekData);
        setBatch(null);
        setAllBatchesForTrek([]);
        const trekOnlyEvent = {
          slug: trekSlug || slug,
          id: null,
          trekId: trekData.id,
          noUpcomingBatches: true,
          title: trekData.name,
          shortDescription: trekData.tagline || '',
          description: trekData.description || '',
          imageUrl: trekData.media?.[0]?.url || null,
          price: trekData.packages?.find?.((p) => p?.isMain)?.price ?? trekData.packages?.[0]?.price ?? null
        };
        setEvent(trekOnlyEvent);
        setLoading(false);
        setInitialBatchLoaded(true);
        finishResolve(trekOnlyEvent);
        return true;
      } catch {
        return false;
      }
    };

    if (batchId) {
      const batchRouteEvent = { id: batchId, slug };
      setEvent((prev) => {
        if (prev && String(prev.id) === String(batchId)) return prev;
        return prev ? { ...prev, id: batchId, slug: prev.slug || slug } : batchRouteEvent;
      });
      finishResolve(batchRouteEvent);
      // Still fetch all batches by slug so Departure Dates section lists every date
      (async () => {
        try {
          const res = await dynamicAPI.getBatchesByTrekSlug(slug);
          if (cancelled) return;
          const list = Array.isArray(res?.data?.data) ? res.data.data : [];
          setAllBatchesForTrek(list.map(mapApiBatchForTrekList));
        } catch {
          if (!cancelled) setAllBatchesForTrek([]);
        }
      })();
      return () => { cancelled = true; };
    }

    (async () => {
      // Path is a numeric event batch id (e.g. from EventCard) — resolve trek slug + departure list.
      if (!batchId && /^\d+$/.test(String(slug))) {
        try {
          const batchRes = await dynamicAPI.getEventBatchById(slug);
          if (cancelled) return;
          const b = batchRes?.data?.data;
          const trekSlug = b?.trek?.slug;
          if (b?.id && trekSlug) {
            const res = await dynamicAPI.getBatchesByTrekSlug(trekSlug);
            if (cancelled) return;
            const list = Array.isArray(res?.data?.data) ? res.data.data : [];
            const withDuration = list.map(mapApiBatchForTrekList);
            setAllBatchesForTrek(withDuration);
            const initialId = pickInitialBatchIdFromList(withDuration, dateParam, norm) ?? b.id;
            setEvent({ slug: trekSlug, id: initialId });
            return;
          }
        } catch {
          /* fall through to slug / content / events list */
        }
      }

      try {
        const res = await dynamicAPI.getBatchesByTrekSlug(slug);
        if (cancelled) return;
        const list = Array.isArray(res?.data?.data) ? res.data.data : [];
        const withDuration = list.map(mapApiBatchForTrekList);
        setAllBatchesForTrek(withDuration);
        const firstId = pickInitialBatchIdFromList(withDuration, dateParam, norm);
        if (firstId) {
          const slugEvent = { slug, id: firstId };
          setEvent(slugEvent);
          finishResolve(slugEvent);
          return;
        }
        // Trek exists but no upcoming dates — still show full trek content.
        const trekIdFromApi = res?.data?.trekId;
        if (trekIdFromApi && (await loadTrekOnlyPage(trekIdFromApi, res?.data?.trekSlug || slug))) {
          return;
        }
      } catch {
        if (cancelled) return;
      }
      // Fallback: content API or events list
      try {
        const res = await contentAPI.getEventBySlug(slug);
        if (cancelled) return;
        const data = res?.data?.data;
        if (data && (data.id || data.trekId || data.noUpcomingBatches || data.idType === 'trek')) {
          if (data.noUpcomingBatches || data.idType === 'trek') {
            const trekId = data.trekId || (data.idType === 'trek' ? data.id : null);
            if (trekId && (await loadTrekOnlyPage(trekId, data.slug || slug))) return;
          }
          if (!data.id) {
            finishResolve(null);
            return;
          }
          let finalEvent = data;
          if (dateParam && events.length) {
            const batchesForSlug = events.filter((e) => e.slug === (data.slug || slug));
            const match = batchesForSlug.find((b) => norm(b.duration?.split(' - ')[0]) === dateParam);
            if (match) finalEvent = { ...data, id: match.id };
          }
          setEvent(finalEvent);
          finishResolve(finalEvent);
          return;
        }
      } catch {
        // ignore
      }
      if (cancelled) return;
      const found = events.find((e) => e.slug === slug || e.id === slug);
      let finalEvent = found || null;
      if (finalEvent && dateParam && events.length) {
        const batchesForSlug = events.filter((e) => e.slug === (finalEvent.slug || slug));
        const match = batchesForSlug.find((b) => norm(b.duration?.split(' - ')[0]) === dateParam);
        if (match) finalEvent = { ...finalEvent, id: match.id };
      }
      setEvent(finalEvent);
      finishResolve(finalEvent);
    })();
    return () => { cancelled = true; };
  }, [slug, batchId, dateParam, eventsLoading]);

  // If slug APIs found nothing, retry once the global events list has loaded.
  useEffect(() => {
    if (event?.id || event?.noUpcomingBatches) {
      if (!eventResolveDone) setEventResolveDone(true);
      return;
    }
    if (eventsLoading || eventResolveDone) return;
    const norm = (s) => (s || '').split('T')[0];
    const found = events.find((e) => e.slug === slug || e.id === slug);
    if (!found) {
      setEventResolveDone(true);
      return;
    }
    let finalEvent = found;
    if (dateParam && events.length) {
      const batchesForSlug = events.filter((e) => e.slug === (found.slug || slug));
      const match = batchesForSlug.find((b) => norm(b.duration?.split(' - ')[0]) === dateParam);
      if (match) finalEvent = { ...found, id: match.id };
    }
    setEvent(finalEvent);
    setEventResolveDone(true);
  }, [event?.id, event?.noUpcomingBatches, events, eventsLoading, eventResolveDone, slug, dateParam]);

  useEffect(() => {
    // Trek-only pages (no upcoming dates) already loaded trek in resolve step.
    if (event?.noUpcomingBatches) {
      return undefined;
    }
    if (!event?.id) {
      setBatch(null);
      setTrek(null);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      // Only show full-page loader on very first batch load
      if (!initialBatchLoaded) {
        setLoading(true);
      }
      try {
        const batchRes = await dynamicAPI.getEventBatchById(event.id);
        const batchData = batchRes.data?.data;
        if (cancelled || !batchData) return;
        setBatch(batchData);
        // Use trek from batch response (already included); skip getTrekById to avoid 502/CORS on treks/:id
        if (!cancelled && batchData.trek) setTrek(batchData.trek);
      } catch {
        if (!cancelled) setBatch(null);
      } finally {
        if (!cancelled && !initialBatchLoaded) {
          setLoading(false);
          setInitialBatchLoaded(true);
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, [event?.id, event?.noUpcomingBatches]);

  // When we have ?date= and the batch list loads, point event at that batch. Do not re-run when the user picks
  // another day in the UI (that only changes batch/event) — deps must not include batch?.startDate or event?.id
  // or we overwrite the selection with the stale ?date= from EventCard "More Details".
  useEffect(() => {
    if (!dateParam || !allBatchesForTrek.length) return;
    const norm = (s) => (s || '').split('T')[0];
    const match = allBatchesForTrek.find((b) => norm(b.startDate) === dateParam);
    if (!match) return;
    setEvent((prev) => {
      if (!prev) return prev;
      if (String(prev.id) === String(match.id)) return prev;
      return { ...prev, id: match.id };
    });
  }, [dateParam, allBatchesForTrek]);

  const handleBookNow = () => {
    if (
      event?.noUpcomingBatches ||
      (!batch?.id && allBatchesForTrek.length === 0)
    ) {
      window.alert(
        'No upcoming dates for this trek right now. Please request a callback and we’ll help you plan.'
      );
      return;
    }
    const batchIdStr = String(batch?.id || event?.id || '');
    const batchMeta = allBatchesForTrek.find((x) => String(x.id) === batchIdStr);
    if (departureDateDisabledForSelectedPackage(batchMeta, effectiveJoinFromId)) {
      const msg =
        departureDateTitleForSelectedPackage(batchMeta, effectiveJoinFromId) || FULL_DEPARTURE_TOOLTIP;
      window.alert(`${msg} Choose another date or request a callback.`);
      return;
    }
    const so = stayOptionsList.length > 0 ? stayOptionAvailability(batch, stayOption) : { disabled: false };
    if (so.disabled) {
      window.alert(so.tooltip || STAY_UNAVAILABLE_TOOLTIP);
      return;
    }
    if (!isAuthenticated) {
      const targetPath = batch?.id ? `/book/${batch.id}` : '/bookings';
      const redirectParams = new URLSearchParams();
      if (effectiveTravelPackage) redirectParams.set('travelPackage', String(effectiveTravelPackage));
      if (stayOption) redirectParams.set('stayOption', String(stayOption));
      if (effectiveJoinFromId != null && String(effectiveJoinFromId).trim()) {
        redirectParams.set('joinFromPackageId', String(effectiveJoinFromId));
      }
      if (trek?.slug || slug) redirectParams.set('eventSlug', String(trek?.slug || slug));
      redirectParams.set('fromEvent', '1');
      const from = redirectParams.toString() ? `${targetPath}?${redirectParams.toString()}` : targetPath;
      navigate('/login', { state: { from } });
      return;
    }
    if (batch?.id) {
      navigate(`/book/${batch.id}`, {
        state: {
          travelPackage: effectiveTravelPackage,
          stayOption,
          joinFromPackageId: effectiveJoinFromId,
          fromEvent: true,
          eventSlug: trek?.slug || slug,
          freshBooking: true
        }
      });
    } else navigate('/bookings');
  };

  const handleCallbackSubmit = async (e) => {
    e.preventDefault();
    if (!callbackForm.name?.trim()) {
      window.alert('Please enter your name.');
      return;
    }
    if (!isValidMobile(callbackForm.phone)) {
      window.alert('Please enter a valid 10-digit mobile number.');
      return;
    }
    setCallbackSubmitting(true);
    try {
      await contentAPI.submitCallbackRequest({
        name: callbackForm.name.trim(),
        phone: normalizeMobile(callbackForm.phone),
        email: callbackForm.email?.trim() || undefined,
        reason: callbackForm.reason?.trim() || undefined,
        message: callbackForm.message?.trim() || undefined,
        batchId: batch?.id || undefined,
        eventSlug: (trek?.slug || slug || '').trim() || undefined
      });
      setCallbackSuccess(true);
      setCallbackForm({ name: '', phone: '', email: '', reason: '', message: '' });
      setTimeout(() => {
        setCallbackModalOpen(false);
        setCallbackSuccess(false);
      }, 1500);
    } catch (err) {
      window.alert(err.response?.data?.message || 'Failed to submit. Please try again.');
    } finally {
      setCallbackSubmitting(false);
    }
  };

  const openPdfModal = () => {
    setPdfModalOpen(true);
    setPdfError('');
    setPdfForm({ name: '', mobile: '' });
  };

  const closePdfModal = () => {
    if (pdfSubmitting) return;
    setPdfModalOpen(false);
    setPdfError('');
  };

  const handleSelectBatchDate = (nextBatchId) => {
    if (!nextBatchId || !event?.slug) return;
    const idStr = String(nextBatchId);
    if (String(event?.id) === idStr) return;
    setEvent((prev) => (prev ? { ...prev, id: nextBatchId, slug: prev.slug || event.slug } : { id: nextBatchId, slug: event.slug }));
    const nextParams = new URLSearchParams(searchParams);
    
    const selectedBatch = allBatchesForTrek.find((b) => String(b.id) === idStr);
    if (selectedBatch?.startDate) {
      nextParams.set('date', selectedBatch.startDate.split('T')[0]);
      nextParams.delete('batch');
    } else {
      nextParams.set('batch', idStr);
      nextParams.delete('date');
    }
    
    setSearchParams(nextParams, { replace: true });
  };

  const handlePdfSubmit = async (e) => {
    e.preventDefault();
    setPdfError('');
    if (!pdfForm.name.trim()) {
      setPdfError('Name is required.');
      return;
    }
    if (!isValidMobile(pdfForm.mobile)) {
      setPdfError('Please enter a valid 10-digit mobile number.');
      return;
    }
    const brochureUrl =
      siteSettings?.event_consultant_pdf_url || batch?.trek?.brochureUrl || trek?.brochureUrl || null;
    const url = brochureUrl && String(brochureUrl).trim() ? String(brochureUrl).trim() : '';
    const cdnUrl = resolveBrochureCdnUrl(url);
    const trekName = (batch?.trek?.name || event?.title || trek?.name || '').trim() || undefined;
    // Open tab during the click gesture — browsers block this after await.
    const popup = cdnUrl ? openBrochurePlaceholderWindow() : null;
    setPdfSubmitting(true);
    try {
      await contentAPI.submitPdfRequest({
        name: pdfForm.name.trim(),
        mobile: normalizeMobile(pdfForm.mobile),
        eventSlug: (trek?.slug || slug || '').trim() || null,
        eventTitle: (batch?.trek?.name || event?.title || '').trim() || null
      });
      setPdfModalOpen(false);
      setPdfForm({ name: '', mobile: '' });
      if (cdnUrl) {
        await downloadBrochureFromUrl(url, { name: trekName, popup });
      } else {
        if (popup && !popup.closed) {
          try {
            popup.close();
          } catch {
            /* ignore */
          }
        }
        window.alert(
          "Thanks! A brochure PDF isn't uploaded for this trek yet — our team will share it with you shortly."
        );
      }
    } catch (err) {
      if (popup && !popup.closed) {
        try {
          popup.close();
        } catch {
          /* ignore */
        }
      }
      setPdfError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setPdfSubmitting(false);
    }
  };

  const batchesForTrek = event ? events.filter((e) => e.slug === event.slug) : [];
  const todayStr = new Date().toISOString().slice(0, 10);
  const trekBatchesList = allBatchesForTrek.length
    ? allBatchesForTrek.filter((b) => b.startDate >= todayStr)
    : batchesForTrek;
  const selectedBatch = batch || (event ? { startDate: event.duration?.split(' - ')[0], endDate: event.duration?.split(' - ')[1] } : null);
  const itineraryStartDate = selectedBatch?.startDate ? new Date(selectedBatch.startDate) : null;
  const hasValidItineraryStartDate = !!(itineraryStartDate && !Number.isNaN(itineraryStartDate.getTime()));
  // Derive duration from batch date range first (so we never show "1 Days 0 Nights"), then package, itinerary, fallback
  let durationDays = null;
  if (batch?.startDate && batch?.endDate) {
    const start = new Date(batch.startDate);
    const end = new Date(batch.endDate);
    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
      const diff = Math.round((end - start) / (24 * 60 * 60 * 1000)) + 1;
      if (diff >= 1) durationDays = diff;
    }
  }
  if (durationDays == null) {
    const primaryPackage = (batch?.trek?.packages || trek?.packages || [])[0];
    if (primaryPackage) {
      const pkg = primaryPackage;
      if (pkg.durationDays != null && Number(pkg.durationDays) > 0) {
        durationDays = Number(pkg.durationDays);
      } else if (pkg.endDayOffset != null && pkg.startDayOffset != null) {
        durationDays = Math.max(1, Number(pkg.endDayOffset) - Number(pkg.startDayOffset) + 1);
      } else if (pkg.endDay != null && pkg.startDay != null) {
        durationDays = Math.max(1, Number(pkg.endDay) - Number(pkg.startDay) + 1);
      }
    }
  }
  const allScheduleDays = (trek?.scheduleDays || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  if (durationDays == null && allScheduleDays.length > 0) {
    durationDays = allScheduleDays.length;
  }
  if (durationDays == null || !Number.isFinite(durationDays) || durationDays <= 0) {
    durationDays = 7;
  }
  const durationNights = Math.max(0, durationDays - 1);
  const attractions = (batch?.trek?.attractions?.length ? batch.trek.attractions : trek?.attractions) || [];
  const webstories = (batch?.trek?.webstories?.length ? batch.trek.webstories : trek?.webstories) || [];
  const feeIncludesRaw = batch?.trek?.feesIncludes?.length
    ? batch.trek.feesIncludes
    : (trek?.feesIncludes || []);
  const inclusions = [...feeIncludesRaw]
    .filter((f) => f && String(f.name || f.description || '').trim())
    .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
  const exclusions = trek?.instructions || [];
  const stayDetails = (batch?.trek || trek)?.stayDetails || [];
  const media = trek?.media || batch?.trek?.media || [];
  const videos = trek?.videos || batch?.trek?.videos || [];
  const experienceGalleryBase = (trek?.experienceGallery || batch?.trek?.experienceGallery || [])
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const galleryVideoIds = new Set(
    experienceGalleryBase
      .filter((item) => item?.type === 'video' && item?.videoId)
      .map((item) => String(item.videoId))
  );
  const videoGalleryItems = videos
    .filter((v) => v?.videoId && !galleryVideoIds.has(String(v.videoId)))
    .map((v, idx) => ({
      id: `video-${v.id ?? idx}-${v.videoId}`,
      type: 'video',
      videoId: v.videoId,
      title: v.title || 'Trek video',
      order: 100000 + idx
    }));
  const experienceGallery = [...experienceGalleryBase, ...videoGalleryItems]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const ATTRACTIONS_FIRST_ROW = 4;
  const visibleAttractions =
    attractionsGridDesktop && !attractionsExpanded
      ? attractions.slice(0, ATTRACTIONS_FIRST_ROW)
      : attractions;
  const hasMoreAttractions = attractionsGridDesktop && attractions.length > ATTRACTIONS_FIRST_ROW;

  const visibleExperienceGallery = galleryExpanded
    ? experienceGallery
    : experienceGallery.slice(0, EVENT_DETAIL_GALLERY_COLLAPSED_COUNT);
  const hasMoreGallery = experienceGallery.length > EVENT_DETAIL_GALLERY_COLLAPSED_COUNT;

  const heroImageUrl = trekDisplayImageUrl(
    batch?.trek?.media?.[0]?.url || batch?.trek?.brochureUrl || event?.imageUrl || FALLBACK_EVENT_IMAGE,
    IMAGE_DISPLAY_WIDTH.hero
  );
  const heroImages = media.length > 0 ? media : [{ id: 'main', url: heroImageUrl }];
  const heroItems = heroImages.map((m) => ({
    type: 'image',
    id: m.id,
    url: trekDisplayImageUrl(m.url, IMAGE_DISPLAY_WIDTH.hero),
    rawUrl: m.url
  }));
  heroGalleryLenRef.current = heroItems.length;
  const activeHero = heroItems[heroIndex] || { type: 'image', url: heroImageUrl };
  const heroMoreCount = Math.max(0, heroItems.length - 5);

  useEffect(() => {
    if (heroItems.length <= 1) return;
    const isLast = heroIndex === heroItems.length - 1;
    const delay = isLast ? 2200 : 4200;
    const timer = setTimeout(() => {
      setHeroIndex((prev) => (prev + 1) % heroItems.length);
    }, delay);
    return () => clearTimeout(timer);
  }, [heroIndex, heroItems.length]);

  useEffect(() => {
    if (heroIndex >= heroItems.length && heroItems.length > 0) {
      setHeroIndex(0);
    }
  }, [heroIndex, heroItems.length]);

  let faqs = [];
  if (trek?.faqs) {
    try {
      const parsed = typeof trek.faqs === 'string' ? JSON.parse(trek.faqs) : trek.faqs;
      if (Array.isArray(parsed) && parsed.length > 0) faqs = parsed.filter((f) => f && (f.q || f.a));
    } catch {
      faqs = [];
    }
  }

  const formatInr = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;
  // Merge packages from batch.trek and trek (dedupe by id) so we never miss packages from either API
  const batchPackages = Array.isArray(batch?.trek?.packages) ? batch.trek.packages : [];
  const trekPackages = Array.isArray(trek?.packages) ? trek.packages : [];
  const byId = new Map();
  [...batchPackages, ...trekPackages].forEach((p) => {
    const id = p.id != null ? String(p.id) : null;
    if (id && !byId.has(id)) byId.set(id, p);
  });
  const rawPackages = filterPackagesForLocalCamps(Array.from(byId.values()), trekForSeo || trek || batch?.trek);
  const packages = [...rawPackages].sort(sortTrekPackagesForDisplay);
  const mainPackagePrice = packages.find((p) => p.isMain)?.price ?? packages[0]?.price;
  const anyPackagePrice = packages.length
    ? Math.max(0, ...packages.map((p) => Number(p?.price) || 0))
    : 0;
  const price = Number(mainPackagePrice) || anyPackagePrice || Number(event?.priceStartsFrom ?? event?.price ?? 0) || 0;
  // Include all packages that are not explicitly hidden (handle both .hidden and .hide from API)
  const visibleJoinPackages = packages.filter(
    (p) => p.isEnabled !== false && !(p.hidden === true || p.hide === true)
  );
  const mainPackageForDuration = packages.find((p) => p.isMain) || packages[0] || null;
  const joinFromPackages = visibleJoinPackages.map((p) => {
    const daysNum = resolveJoinPackageDays(p, {
      mainPackage: mainPackageForDuration,
      trekDurationDays: durationDays,
      allScheduleDays
    });
    const pPrice = Number(p.price) || Number(price) || 0;
    return {
      id: p.id,
      name: p.name,
      description: p.description || p.name,
      days: `${daysNum} Day${daysNum !== 1 ? 's' : ''}`,
      price: pPrice,
      originalPrice: p.originalPrice != null ? Number(p.originalPrice) : undefined,
      image: p.imageUrl || p.image || 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=200',
      priority: Number(p.priority) || 0,
      isMain: !!p.isMain
    };
  });
  const joinCitiesFallback = [
    { id: 'delhi', name: 'Delhi', description: 'Delhi', days: `${durationDays} Days`, price: Math.max(0, price + 0), image: 'https://images.unsplash.com/photo-1587474260584-136574528ed5?w=400' },
    { id: 'dehradun', name: 'Dehradun', description: 'Dehradun', days: `${Math.max(1, durationDays - 1)} Days`, price: Math.max(0, price - 1500), image: 'https://images.unsplash.com/photo-1596464716127-f2a82984de30?w=400' },
    { id: 'sankri', name: 'Sankri', description: 'Sankri', days: `${Math.max(1, durationDays - 2)} Days`, price: Math.max(0, price - 4000), image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400' },
    { id: 'ahmedabad', name: 'Ahmedabad', description: 'Ahmedabad', days: `${durationDays + 1} Days`, price: Math.max(0, price + 2500), image: 'https://images.unsplash.com/photo-1570168007204-dfb528c6958f?w=400' }
  ];
  // Never invent join cities for a real trek (e.g. Spiti with no upcoming dates).
  const hasRealTrek = Boolean(trek?.id || batch?.trek?.id);
  const flatPackages =
    joinFromPackages.length > 0 ? joinFromPackages : hasRealTrek ? [] : joinCitiesFallback;
  const joinLocations = (() => {
    const byName = {};
    flatPackages.forEach((p) => {
      if (!byName[p.name]) byName[p.name] = [];
      byName[p.name].push(p);
    });
    const sortJoinCardsInLocation = (a, b) => {
      if (!!b.isMain !== !!a.isMain) return (b.isMain ? 1 : 0) - (a.isMain ? 1 : 0);
      const priA = Number(a.priority) || 0;
      const priB = Number(b.priority) || 0;
      if (priA !== priB) return priA - priB;
      return String(a.id ?? '').localeCompare(String(b.id ?? ''));
    };
    return Object.entries(byName)
      .map(([locationName, pkgs]) => {
        const sortedPkgs = [...pkgs].sort(sortJoinCardsInLocation);
        const sortKey = sortedPkgs.reduce(
          (m, x) => Math.min(m, Number(x.priority) || 0),
          Number.POSITIVE_INFINITY
        );
        return {
          locationName,
          locationOrder: Number.isFinite(sortKey) ? sortKey : 0,
          image: sortedPkgs[0].image,
          days: sortedPkgs[0].days,
          packages: sortedPkgs
        };
      })
      .sort((a, b) => a.locationOrder - b.locationOrder);
  })();
  const effectiveJoinFromId = selectedJoinFromId ?? flatPackages[0]?.id;
  const selectedJoinFromPackage = flatPackages.find((c) => String(c.id) === String(effectiveJoinFromId)) || flatPackages[0];
  const effectiveTravelPackage = inferTravelPackageFromText(
    `${selectedJoinFromPackage?.description || ''} ${selectedJoinFromPackage?.name || ''}`
  ) || travelPackage;
  const basePrice = selectedJoinFromPackage?.price ?? price;
  const t = batch?.trek || trek;
  const activePackage = packages.find((p) => String(p.id) === String(selectedJoinFromPackage?.id));
  const logisticsDurationDays = (() => {
    if (activePackage) {
      return resolveJoinPackageDays(activePackage, {
        mainPackage: mainPackageForDuration,
        trekDurationDays: durationDays,
        allScheduleDays
      });
    }
    const label = selectedJoinFromPackage?.days;
    const fromLabel = label != null ? Number(String(label).match(/^(\d+)/)?.[1]) : NaN;
    if (Number.isFinite(fromLabel) && fromLabel > 0) return fromLabel;
    return durationDays;
  })();
  const logisticsRows = buildLogisticsRows(batch?.trek || trek, {
    durationDays: logisticsDurationDays,
    events,
    pageSlug: slug,
    pageEvent: event
  });
  const selectedPackageDurationDays = logisticsDurationDays;
  const selectedPackageDurationNights = Math.max(0, selectedPackageDurationDays - 1);
  const stayOptionsList = Array.isArray(t?.stayOptions) && t.stayOptions.length > 0
    ? t.stayOptions.map((o) => ({ id: String(o.id || ''), name: String(o.name || ''), priceAdjust: Number(o.priceAdjust) || 0 }))
    : [];
  const hasStayOptions = stayOptionsList.length > 0;
  const stayDisplayForIncludes = (() => {
    if (!hasStayOptions) return formatStayOptionLabel(null, stayOption || 'quad');
    const opt = stayOptionsList.find((o) => o.id === stayOption);
    return formatStayOptionLabel(opt?.name, stayOption);
  })();
  const applyStayToken = (text) => String(text || '').replace(/\{stay\}/gi, stayDisplayForIncludes);
  const sidebarFeeIncludes = (() => {
    const rawNew = activePackage?.includesItems ?? activePackage?.includes_items;
    if (Array.isArray(rawNew) && rawNew.length > 0) {
      const sorted = [...rawNew].sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
      const items = sorted
        .map((row) => {
          const title = String(row?.title || '').trim();
          if (!title) return null;
          let valuePart = String(row?.value ?? '').trim();
          if (/\{stay\}/i.test(valuePart)) {
            if (!hasStayOptions) return null;
            valuePart = applyStayToken(valuePart).trim();
          }
          const name = valuePart ? `${title} (${valuePart})` : title;
          return {
            name,
            icon: String(row?.icon || '').trim() || 'briefcase',
            includeTitle: title,
            includeValue: valuePart || null
          };
        })
        .filter(Boolean);
      return items.length ? items : DEFAULT_BOOKING_FEE_INCLUDES;
    }

    // Legacy: old includes_config (label/key rows) — only {stay} is substituted; no travel auto-text
    if (Array.isArray(activePackage?.includesConfig) && activePackage.includesConfig.length > 0) {
      const items = activePackage.includesConfig
        .filter((item) => item && item.enabled !== false && String(item.label || item.key || '').trim())
        .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0))
        .map((item) => {
          let label = String(item.label || item.key || '').trim();
          if (/\{stay\}/i.test(label)) {
            if (!hasStayOptions) return null;
            label = applyStayToken(label).trim();
          }
          return { name: label, icon: String(item.icon || '').trim() || 'briefcase' };
        })
        .filter(Boolean);
      return items.length ? items : DEFAULT_BOOKING_FEE_INCLUDES;
    }

    if (activePackage && typeof activePackage.includesTravelling === 'boolean') {
      const items = [];
      if (activePackage.includesTravelling) items.push({ name: 'Travelling', icon: 'briefcase' });
      if (activePackage.includesAccommodation && hasStayOptions && stayDisplayForIncludes) {
        items.push({
          name: `Accommodation (${stayDisplayForIncludes})`,
          icon: 'building',
          includeTitle: 'Accommodation',
          includeValue: stayDisplayForIncludes
        });
      }
      if (activePackage.includesFirstAid) items.push({ name: 'First Aid', icon: 'plus' });
      if (activePackage.includesGst) items.push({ name: 'GST', icon: 'file-text' });
      return items.length ? items : DEFAULT_BOOKING_FEE_INCLUDES;
    }
    return inclusions.length > 0 ? inclusions : DEFAULT_BOOKING_FEE_INCLUDES;
  })();

  const pkgForItinerary = activePackage
    ? { ...activePackage, name: activePackage.name || selectedJoinFromPackage?.name }
    : selectedJoinFromPackage?.name
      ? { name: selectedJoinFromPackage.name, enabledScheduleIds: [] }
      : null;
  const scheduleDays = pkgForItinerary
    ? resolveScheduleDaysForPackage(pkgForItinerary, allScheduleDays, visibleJoinPackages)
    : allScheduleDays;

  // Batch start date = departure for the reference join package (main, else first visible). Other packages shift by (start_day − ref start_day); rows stay consecutive calendar days.
  const referencePackageForItineraryDates =
    visibleJoinPackages.find((p) => p.isMain) || visibleJoinPackages[0] || null;
  const refStartDayForDates = readPackageStartDay(referencePackageForItineraryDates);
  const activeStartDayForDates = activePackage
    ? readPackageStartDay(activePackage)
    : refStartDayForDates;
  const itineraryFirstDayShift =
    referencePackageForItineraryDates && activePackage
      ? activeStartDayForDates - refStartDayForDates
      : 0;

  const itineraryDisplayDayNumbers = buildItineraryDisplayDayNumbers(scheduleDays);

  const getItineraryDateLabel = (displayDayNumber) => {
    if (!hasValidItineraryStartDate || !selectedBatch?.startDate) return '';
    if (displayDayNumber == null || !Number.isFinite(Number(displayDayNumber))) return '';
    const key = addDaysToDateKey(
      selectedBatch.startDate,
      itineraryFirstDayShift + itineraryDateOffsetFromDisplayDay(displayDayNumber)
    );
    if (!key) return '';
    const p = parseDateParts(key);
    if (!p) return '';
    const d = new Date(p.year, p.month - 1, p.day);
    const weekday = d.toLocaleDateString('en-IN', { weekday: 'long' });
    const fullDate = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${weekday}, ${fullDate}`;
  };

  const travelBusAdjust = Number(activePackage?.travelBusAdjust ?? t?.travelBusAdjust ?? 0);
  const travelTrainAdjust = Number(activePackage?.travelTrainAdjust ?? t?.travelTrainAdjust ?? 0);
  const travelFlightAdjust = Number(activePackage?.travelFlightAdjust ?? t?.travelFlightAdjust ?? 0);
  const stayQuadAdjust = Number(activePackage?.stayQuadAdjust ?? t?.stayQuadAdjust ?? 0);
  const stayTripleAdjust = Number(activePackage?.stayTripleAdjust ?? t?.stayTripleAdjust ?? 0);
  const stayDoubleAdjust = Number(activePackage?.stayDoubleAdjust ?? t?.stayDoubleAdjust ?? 0);

  const travelAdjust =
    travelBusAdjust * (effectiveTravelPackage === 'bus' ? 1 : 0) +
    travelTrainAdjust * (effectiveTravelPackage === 'train' ? 1 : 0) +
    travelFlightAdjust * (effectiveTravelPackage === 'flight' ? 1 : 0);
  const stayAdjust = stayOptionsList.length > 0
    ? (stayOptionsList.find((o) => String(o.id) === String(stayOption))?.priceAdjust ?? 0)
    : (stayQuadAdjust * (stayOption === 'quad' ? 1 : 0) +
       stayTripleAdjust * (stayOption === 'triple' ? 1 : 0) +
       stayDoubleAdjust * (stayOption === 'double' ? 1 : 0));
  const displayPrice = Math.max(0, basePrice + travelAdjust + stayAdjust);

  useEffect(() => {
    if (!stayOptionsList.length) return;
    const isStayAvail = (o) => {
      const m = batch?.availabilityMeta?.stayOptions?.[String(o.id)];
      if (!m) return true;
      if (m.enabled === false || m.isFull) return false;
      return true;
    };
    const hasId = stayOptionsList.some((o) => o.id === stayOption);
    const cur = stayOptionsList.find((o) => o.id === stayOption);
    const curOk = hasId && cur && isStayAvail(cur);
    if (!curOk) {
      const firstOk = stayOptionsList.find(isStayAvail);
      setStayOption((firstOk ?? stayOptionsList[0]).id);
    }
  }, [stayOptionsList, stayOption, batch?.id, batch?.availabilityMeta, t?.id]);

  useEffect(() => {
    setExpandedItineraryDayId(null);
  }, [effectiveJoinFromId]);

  /* Only admin-picked similar treks; no fallback. Section (heading + row) hidden when empty or all invalid. */
  const rawSimilarTreks = (batch?.trek || trek)?.similarTreks;
  const similarEvents =
    Array.isArray(rawSimilarTreks) && rawSimilarTreks.length > 0
      ? rawSimilarTreks
          .filter((s) => s && typeof s === 'object')
          .map((s) => ({
            id: s.id,
            slug: s.slug,
            title: s.name,
            imageUrl: s.imageUrl,
            media: s.media,
            difficulty: s.difficulty,
            shortDescription: s.tagline,
            description: s.description,
            duration: s.duration,
            durationDays: s.durationDays,
            startDate: s.startDate,
            endDate: s.endDate,
            departureDates: s.departureDates,
            location: s.location,
            region: s.location,
            price: s.price,
            packages: s.packages,
            brochureUrl: s.brochureUrl
          }))
          .filter((e) => {
            const hasLink =
              (e.slug != null && String(e.slug).trim() !== '') || (e.id != null && String(e.id).trim() !== '');
            const hasLabel =
              (e.title != null && String(e.title).trim() !== '') ||
              (e.shortDescription != null && String(e.shortDescription).trim() !== '');
            return hasLink && hasLabel;
          })
      : [];

  /* Departure dates: batches with startDate, grouped by month for design (use full list when available) */
  const batchesWithDates = (trekBatchesList || [])
    .map((b) => {
      const baseStart = b.startDate || b.duration?.split(' - ')[0];
      const adjusted = addDaysToDateKey(baseStart, itineraryFirstDayShift);
      return {
        ...b,
        startDate: adjusted || baseStart,
        baseStartDate: baseStart,
        isFull: b.isFull === true
      };
    })
    .filter((b) => parseDateParts(b.startDate))
    .sort((a, b) => toDateKey(a.startDate).localeCompare(toDateKey(b.startDate)));
  const departureYearMonths = [...new Set(
    batchesWithDates
      .map((b) => getYearMonthKeyFromDate(b.startDate))
      .filter(Boolean)
  )].sort((a, b) => a.localeCompare(b));
  const selectedBatchId = String(batch?.id || event?.id || batchId || '');
  const currentBatchStart = batchesWithDates.find((b) => String(b.id) === selectedBatchId)?.startDate ?? batchesWithDates[0]?.startDate;
  const selectedYearMonth =
    currentBatchStart != null ? getYearMonthKeyFromDate(currentBatchStart) : departureYearMonths[0];
  const batchesInSelectedMonth = batchesWithDates.filter(
    (b) => getYearMonthKeyFromDate(b.startDate) === selectedYearMonth
  );

  const description = batch?.trek?.description || event?.description || 'Join us for an unforgettable adventure.';
  const importantUpdate =
    String(batch?.trek?.importantUpdate ?? trek?.importantUpdate ?? '').trim();

  useEffect(() => {
    // Reset and re-evaluate truncation when the description changes.
    setAboutExpanded(false);
  }, [description]);

  useEffect(() => {
    if (aboutExpanded) return; // Only need truncation check in collapsed state.
    const el = aboutTextRef.current;
    if (!el) return;

    const compute = () => {
      const node = aboutTextRef.current;
      if (!node) return;
      // If content is longer than the clamped box, scrollHeight will be larger than clientHeight.
      const isTrunc = node.scrollHeight - node.clientHeight > 1;
      setAboutIsTruncated(isTrunc);
    };

    compute();
    // Re-check after layout/paint settles (fonts/content can shift after first sync measure)
    let raf2 = null;
    const raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(compute);
    });
    if (typeof window !== 'undefined') window.addEventListener('resize', compute);
    return () => {
      if (typeof window !== 'undefined') {
        window.cancelAnimationFrame(raf1);
        if (raf2) window.cancelAnimationFrame(raf2);
      }
      if (typeof window !== 'undefined') window.removeEventListener('resize', compute);
    };
  }, [description, aboutExpanded]);

  const pageStillLoading = !eventResolveDone || loading;

  if (pageStillLoading) {
    return (
      <div className="event-detail-page">
        <div className="event-detail-loading">
          <LoadingIndicator label="Loading event details..." />
        </div>
      </div>
    );
  }

  if (!event?.id && !event?.noUpcomingBatches && !trek?.id) {
    return (
      <div className="event-detail-page">
        <div className="event-detail-empty">
          <h2>Event Not Found</h2>
          <p>The event you're looking for doesn't exist.</p>
          <Link to="/events" className="event-detail-btn-primary">Browse All Events</Link>
        </div>
      </div>
    );
  }

  const policyTrek = batch?.trek || trek;
  const quickPolicyLinks = parseQuickPolicyLinksFromTrek(policyTrek);

  const trekName = batch?.trek?.name || event.title;
  const trekTagline = batch?.trek?.tagline || event.shortDescription;

  const handleShareEvent = () => {
    setShareModalOpen(true);
  };

  const imageUrl = batch?.trek?.media?.[0]?.url || batch?.trek?.brochureUrl || event.imageUrl || 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200';
  const categoryName = batch?.trek?.category?.name || event.category || '';
  const trekConsultant = batch?.trek?.consultant || trek?.consultant || null;
  const consultantName = trekConsultant
    ? [trekConsultant.firstName, trekConsultant.lastName].filter(Boolean).join(' ').trim()
    : '';
  /** Call / WhatsApp use office lines from site labels (not consultant personal / batch group links). */
  const officeTelHref = resolveOfficeTelHref(siteSettings?.contact_phone);
  const officeWhatsAppHref = resolveOfficeWhatsAppHref(siteSettings?.contact_phone);
  const shouldShowConsultantCard = Boolean(
    trekConsultant && (consultantName || trekConsultant?.phone || trekConsultant?.avatar)
  );
  const aboutTextNormalizedLength = String(description || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim().length;
  const shouldShowAboutReadMore = aboutExpanded || aboutIsTruncated || aboutTextNormalizedLength > 260;

  const trekBadges = (() => {
    const tRef = batch?.trek || trek;
    if (Array.isArray(tRef?.badges) && tRef.badges.length > 0) {
      return tRef.badges
        .filter((b) => b && (b.label || b.text))
        .map((b) => ({
          label: String(b.label || b.text || '').trim(),
          icon: String(b.icon || '').trim().toLowerCase()
        }))
        .filter((b) => b.label);
    }
    return [];
  })();

  const selectedListBatch = allBatchesForTrek.find((x) => String(x.id) === selectedBatchId);
  const departureBlockedForBook = departureDateDisabledForSelectedPackage(
    selectedListBatch,
    effectiveJoinFromId
  );
  const stayOptAvailForBook =
    stayOptionsList.length > 0 ? stayOptionAvailability(batch, stayOption) : { disabled: false, tooltip: undefined };
  // Travelling package is always selectable; availability is reflected on departure dates + Book Now.
  const noUpcomingDates =
    Boolean(event?.noUpcomingBatches) ||
    (!batch?.id && allBatchesForTrek.length === 0 && eventResolveDone && initialBatchLoaded);
  const bookNowBlocked =
    noUpcomingDates || departureBlockedForBook || stayOptAvailForBook.disabled;
  const bookNowTitle = noUpcomingDates
    ? 'No upcoming dates for this trek. Request a callback and we’ll help you plan.'
    : departureBlockedForBook
      ? departureDateTitleForSelectedPackage(selectedListBatch, effectiveJoinFromId) || FULL_DEPARTURE_TOOLTIP
      : stayOptAvailForBook.disabled
        ? stayOptAvailForBook.tooltip
        : undefined;

  const renderPriceSidebarCard = (extraClass = '', bookRef) => (
    <div className={['event-detail-sidebar-card', extraClass].filter(Boolean).join(' ')}>
      <p className="event-detail-price-main">
        {formatInr(displayPrice)} <span>/ Person</span>
      </p>
      <div className="event-detail-includes">
        <h4>Includes</h4>
        <div className="event-detail-includes-grid">
          {sidebarFeeIncludes.map((item, idx) => {
            const vis = getIncludeIconDisplay(item.icon);
            const Icon = vis.mode === 'lucide' ? getIncludeSidebarLucideComponent(item.icon) : null;
            const label = (item.name || item.description || '').trim();
            if (!label) return null;
            const incTitle = item.includeTitle != null ? String(item.includeTitle).trim() : '';
            const incValue = item.includeValue != null ? String(item.includeValue).trim() : '';
            const useTitleValue = Boolean(incTitle && incValue);
            const rowKey = item.id != null ? String(item.id) : `inc-${idx}-${label}`;
            return (
              <span key={rowKey} className="event-detail-include-pill">
                <span className="event-detail-include-pill-icon" aria-hidden>
                  {vis.mode === 'emoji' ? (
                    <span className="event-detail-include-emoji">{vis.text}</span>
                  ) : (
                    <Icon size={22} />
                  )}
                </span>
                <span className="event-detail-include-pill-label">
                  {useTitleValue ? (
                    <>
                      <span className="event-detail-include-pill-strong">{incTitle}</span>
                      <span className="event-detail-include-pill-detail"> ({incValue})</span>
                    </>
                  ) : (
                    label
                  )}
                </span>
              </span>
            );
          })}
        </div>
      </div>

      <button
        type="button"
        className="event-detail-book-now"
        onClick={handleBookNow}
        ref={bookRef}
        disabled={bookNowBlocked}
        title={bookNowTitle}
      >
        Book Now
      </button>
    </div>
  );

  return (
    <div className="event-detail-page">
      {/* Hero Section (Asymmetric Bento Grid) */}
      <div className="ed-hero-container">
        <Breadcrumbs
          items={[
            { label: 'Events', path: '/events' },
            { label: trekName || 'Trek Details' }
          ]}
          className="event-detail-breadcrumbs"
        />
        <div className="ed-hero-asymmetric">
          {/* Left Large Image */}
          {heroItems.length > 0 && (
            <div
              className="ed-hero-main"
              onClick={() => {
                if (heroItems[0].type === 'video') setActiveVideoId(heroItems[0].videoId);
                else {
                  setHeroLightboxIndex(0);
                  setHeroLightboxOpen(true);
                }
              }}
              role="presentation"
            >
              <img
                src={heroItems[0].type === 'video' ? heroItems[0].thumbUrl : heroItems[0].url}
                alt={trekName}
                fetchpriority="high"
                onError={(e) => trekImageOnError(e, heroItems[0].rawUrl)}
              />
              {heroItems[0].type === 'video' && <div className="ed-hero-play"><Play size={32} /></div>}
            </div>
          )}

          {/* Right 2x2 Grid */}
          {heroItems.length > 1 && (
            <div className="ed-hero-side">
              {heroItems.slice(1, 5).map((item, idx) => {
                const isFourthThumb = idx === 3;
                const showMoreOverlay = isFourthThumb && heroMoreCount > 0;
                const handleThumbClick = () => {
                  if (item.type === 'video') {
                    setActiveVideoId(item.videoId);
                    return;
                  }
                  if (showMoreOverlay) {
                    setHeroLightboxIndex(Math.min(5, heroItems.length - 1));
                    setHeroLightboxOpen(true);
                    return;
                  }
                  setHeroLightboxIndex(idx + 1);
                  setHeroLightboxOpen(true);
                };
                return (
                  <div
                    key={item.id || idx}
                    className="ed-hero-sub"
                    onClick={handleThumbClick}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleThumbClick();
                      }
                    }}
                    aria-label={
                      showMoreOverlay
                        ? `View all images, ${heroMoreCount} more in gallery`
                        : `Open image ${idx + 2} in gallery`
                    }
                  >
                    <img
                      src={item.type === 'video' ? item.thumbUrl : item.url}
                      alt={`${trekName} ${idx + 2}`}
                      onError={(e) => trekImageOnError(e, item.rawUrl)}
                    />
                    {item.type === 'video' && <div className="ed-hero-play-small"><Play size={20} /></div>}
                    {showMoreOverlay && (
                      <div className="ed-hero-more-overlay" aria-hidden>
                        <div className="ed-hero-more-button">
                          <Images size={20} strokeWidth={1.75} className="ed-hero-more-button-icon" />
                          <span className="ed-hero-more-button-text">View All Images</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Fill empty spots with light gray background if there are less than 5 images */}
              {Array.from({ length: Math.max(0, 4 - heroItems.slice(1, 5).length) }).map((_, idx) => (
                <div key={`empty-${idx}`} className="ed-hero-sub empty-bg"></div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="event-detail-wrap">
        <div className="event-detail-main">
          <div
            className={[
              'event-detail-card',
              'event-title-card',
              trekBadges.length === 0 ? 'event-title-card--compact' : ''
            ].filter(Boolean).join(' ')}
          >
            <div className="event-title-card-stack">
              <p className="event-detail-duration">
                {selectedPackageDurationDays} Day{selectedPackageDurationDays !== 1 ? 's' : ''} / {selectedPackageDurationNights} Night{selectedPackageDurationNights !== 1 ? 's' : ''}
              </p>
              <div className="event-detail-title-block">
                <div className="event-detail-title-row">
                  <h1 className="event-detail-title">{trekName}</h1>
                  <button
                    type="button"
                    className="event-detail-share"
                    onClick={handleShareEvent}
                    aria-label="Share"
                  >
                    <Share2 size={16} />
                    <span className="event-detail-share-label">Share</span>
                  </button>
                </div>
                {trekTagline ? <p className="event-detail-tagline">{trekTagline}</p> : null}
              </div>
              {trekBadges.length > 0 ? (
                <div className="event-detail-badges">
                  {trekBadges.map((badge, idx) => {
                    const rawIcon = String(badge.icon || '').trim();
                    const IconComp = rawIcon ? getIncludeSidebarLucideComponent(rawIcon) : null;
                    return (
                      <span key={`badge-${idx}-${badge.label}`} className="event-detail-badge">
                        {IconComp ? <IconComp size={16} strokeWidth={2} aria-hidden /> : null}
                        {badge.label}
                      </span>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </div>

          {importantUpdate ? (
            <div className="event-detail-important-update" role="note" aria-label="Important update">
              <div className="event-detail-important-update-label">Important Update</div>
              <div className="event-detail-important-update-body">
                <RichContent content={importantUpdate} />
              </div>
            </div>
          ) : null}

          <div className="event-detail-card">
            <h2 className="event-detail-section-title">About The Trek</h2>
            <div className="event-detail-about">
              <div
                ref={aboutTextRef}
                className={aboutExpanded ? 'event-detail-about-body' : 'event-detail-about-body event-detail-about-clamp'}
              >
                <RichContent content={description} />
              </div>
              {shouldShowAboutReadMore && (
                <span
                  className="event-detail-read-more"
                  role="button"
                  tabIndex={0}
                  aria-expanded={aboutExpanded}
                  onClick={() => setAboutExpanded((e) => !e)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') setAboutExpanded((prev) => !prev);
                  }}
                >
                  {aboutExpanded ? (
                    'Read Less'
                  ) : (
                    <>
                      Read More
                      <img
                        src={`${import.meta.env.BASE_URL}assets/arrow-up-right.svg`}
                        alt=""
                        className="event-detail-read-more-icon"
                        aria-hidden
                      />
                    </>
                  )}
                </span>
              )}
            </div>
          </div>

          {logisticsRows.length > 0 && (
            <div className="event-detail-card">
              <h2 className="event-detail-section-title">Trek Information</h2>
              <ul className="event-detail-logistics-grid">
                {logisticsRows.map((row) => {
                  const LogIcon = getIncludeSidebarLucideComponent(row.iconSlug || 'check');
                  return (
                    <li key={row.key} className="event-detail-logistics-item">
                      <span className="event-detail-logistics-icon" aria-hidden>
                        <LogIcon size={22} strokeWidth={2} />
                      </span>
                      <div className="event-detail-logistics-text">
                        <span className="event-detail-logistics-label">{row.label}</span>
                        <span className="event-detail-logistics-value">{row.value}</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {joinLocations.length > 0 && (
          <div className="event-detail-card">
            <h2 className="event-detail-section-title">Join Us From</h2>
            <div className="event-detail-join-grid">
              {joinLocations.map((loc) => {
                const isLocationSelected = loc.packages.some((p) => String(p.id) === String(effectiveJoinFromId));
                const selectedPkgInLoc =
                  loc.packages.find((p) => String(p.id) === String(effectiveJoinFromId)) ||
                  loc.packages[0];
                const locHasEnabled = loc.packages.some((p) => !joinPackageAvailability(batch, p.id).disabled);
                const daysLabel = (selectedPkgInLoc.days || loc.days || '').replace(/\s*Journey\s*$/i, '').trim() || `${durationDays} Days`;
                const cardPrice = Math.max(0, (selectedPkgInLoc.price ?? 0) + stayAdjust);
                const originalPrice = selectedPkgInLoc.originalPrice != null ? Math.max(0, Number(selectedPkgInLoc.originalPrice) + stayAdjust) : null;
                const showStrikethrough = originalPrice != null && originalPrice > cardPrice;
                const cardImage = trekDisplayImageUrl(
                  selectedPkgInLoc.image || loc.image,
                  IMAGE_DISPLAY_WIDTH.thumb
                );
                return (
                  <button
                    key={loc.locationName}
                    type="button"
                    className={`event-detail-join-card ${isLocationSelected ? 'event-detail-join-card-selected' : ''}`}
                    title={
                      !locHasEnabled
                        ? 'No departures for packages from this city on this date — pick another date or package.'
                        : undefined
                    }
                    onClick={() => {
                      const pick =
                        loc.packages.find((p) => String(p.id) === String(effectiveJoinFromId)) ||
                        loc.packages[0];
                      if (pick) setSelectedJoinFromId(pick.id);
                    }}
                  >
                    <div className="event-detail-join-card-image-wrap">
                      <img src={cardImage} alt={loc.locationName} loading="lazy" />
                      <span className="event-detail-join-card-days">{daysLabel}</span>
                    </div>
                    <div className="event-detail-join-card-body">
                      <strong className="event-detail-join-card-location">{loc.locationName}</strong>
                      <div className="event-detail-join-card-price-row">
                        {showStrikethrough && (
                          <span className="event-detail-join-card-original">{formatInr(originalPrice)}</span>
                        )}
                        <span className="event-detail-join-card-price">{formatInr(cardPrice)}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          )}

          {(() => {
            const selectedLocation = joinLocations.find((loc) =>
              loc.packages.some((p) => String(p.id) === String(effectiveJoinFromId))
            );
            if (!selectedLocation || selectedLocation.packages.length === 0) return null;
            return (
              <div className="event-detail-card">
                <h2 className="event-detail-section-title">Travelling Package</h2>
                <div className="event-detail-pills">
                  {selectedLocation.packages.map((pkg) => {
                    const label = (pkg.description || '').trim() || pkg.name;
                    const av = joinPackageAvailability(batch, pkg.id);
                    const softUnavailable = av.disabled;
                    return (
                      <button
                        key={pkg.id}
                        type="button"
                        className={`event-detail-pill ${String(pkg.id) === String(effectiveJoinFromId) ? 'active' : ''}${softUnavailable ? ' event-detail-pill--soft-unavailable' : ''}`}
                        title={
                          av.disabled && av.tooltip
                            ? `${av.tooltip} You can still select this option and pick a departure date where it is available.`
                            : undefined
                        }
                        onClick={() => setSelectedJoinFromId(pkg.id)}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {stayOptionsList.length > 0 && (
            <div className="event-detail-card">
              <h2 className="event-detail-section-title">Stay Options</h2>
              <div className="event-detail-pills">
                {stayOptionsList.map((opt) => {
                  const av = stayOptionAvailability(batch, opt.id);
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      className={`event-detail-pill ${stayOption === opt.id ? 'active' : ''}`}
                      disabled={av.disabled}
                      title={av.tooltip}
                      onClick={() => {
                        if (!av.disabled) setStayOption(opt.id);
                      }}
                    >
                      {opt.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {batchesWithDates.length > 0 ? (
            <div className="event-detail-card event-detail-departure-card">
              <div className="event-detail-departure-intro">
                <h2 className="event-detail-section-title">Departure Dates</h2>
                <p className="event-detail-departure-days-hint">
                  <em>The dates in shadow are either full or not available</em>
                </p>
              </div>
              <div className="event-detail-departure-months">
                {departureYearMonths.map((yearMonthKey) => {
                  const inMonth = batchesWithDates.filter(
                    (b) => getYearMonthKeyFromDate(b.startDate) === yearMonthKey
                  );
                  const firstSelectableInMonth = inMonth.find(
                    (b) => !departureDateDisabledForSelectedPackage(b, effectiveJoinFromId)
                  );
                  const monthHasSelectable = Boolean(firstSelectableInMonth);
                  const isSelected = selectedYearMonth === yearMonthKey;
                  const monthDisabledTitle = monthHasSelectable
                    ? undefined
                    : 'No departures available for your selected option this month.';
                  const monthLabel = departureMonthLabel(yearMonthKey, departureYearMonths, MONTHS);
                  return (
                    <button
                      key={yearMonthKey}
                      type="button"
                      className={`event-detail-month-pill ${isSelected ? 'active' : ''}`}
                      disabled={!monthHasSelectable}
                      title={monthDisabledTitle}
                      onClick={() =>
                        firstSelectableInMonth && handleSelectBatchDate(firstSelectableInMonth.id)
                      }
                    >
                      {isSelected && <Check size={14} className="event-detail-month-check" />}
                      {monthLabel}
                    </button>
                  );
                })}
              </div>
              <div className="event-detail-departure-days">
                {batchesInSelectedMonth.map((b) => {
                  const day = getDayOfMonthFromDate(b.startDate);
                  const isSelected = String(b.id) === selectedBatchId;
                  const dayUnpickable = departureDateDisabledForSelectedPackage(b, effectiveJoinFromId);
                  const dayTitle = departureDateTitleForSelectedPackage(b, effectiveJoinFromId);
                  return (
                    <button
                      key={b.id}
                      type="button"
                      className={`event-detail-day-pill ${isSelected ? 'active' : ''}${dayUnpickable ? ' event-detail-day-pill--full' : ''}`}
                      disabled={dayUnpickable}
                      title={dayTitle}
                      onClick={() => handleSelectBatchDate(b.id)}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                className="event-detail-departure-book"
                onClick={handleBookNow}
                disabled={bookNowBlocked}
                title={bookNowTitle}
              >
                Book Now
              </button>
            </div>
          ) : noUpcomingDates ? (
            <div className="event-detail-card event-detail-departure-card">
              <div className="event-detail-departure-intro">
                <h2 className="event-detail-section-title">Departure Dates</h2>
                <p className="event-detail-departure-days-hint">
                  No upcoming dates right now. You can still explore the trek details below, or request a callback and we’ll help you plan.
                </p>
              </div>
              <button
                type="button"
                className="event-detail-departure-book"
                onClick={handleBookNow}
                disabled={bookNowBlocked}
                title={bookNowTitle}
              >
                Book Now
              </button>
            </div>
          ) : null}

          {renderPriceSidebarCard('event-detail-price-inline-mobile', mobileBookButtonRef)}

          {scheduleDays.length > 0 && (
            <div className="event-detail-card">
              <div className="event-detail-itinerary-head">
                <h2 className="event-detail-section-title">Itinerary</h2>
                <button type="button" className="event-detail-get-pdf" onClick={openPdfModal}>
                  <FileText size={18} />
                  Get PDF
                </button>
              </div>
              {scheduleDays.map((day, i) => {
                const displayDayNumber = itineraryDisplayDayNumbers[i] ?? i;
                const itineraryDateLabel = getItineraryDateLabel(displayDayNumber);
                return (
                <div key={day.id} className="event-detail-itinerary-block">
                  <p className="event-detail-itinerary-meta">
                    Day {displayDayNumber}{itineraryDateLabel ? ` · ${itineraryDateLabel}` : ''}
                  </p>
                  <h3 className="event-detail-itinerary-block-title">{day.name}</h3>
                  <div className="event-detail-itinerary-brief-row">
                    {day.brief ? (
                      <RichContent content={day.brief} className="event-detail-itinerary-block-brief" />
                    ) : (
                      <span />
                    )}
                    {(day.description || day.brief) && (
                      <button
                        type="button"
                        className="event-detail-know-more"
                        onClick={() => setExpandedItineraryDayId((prev) => (prev === day.id ? null : day.id))}
                        aria-expanded={expandedItineraryDayId === day.id}
                      >
                        {expandedItineraryDayId === day.id ? 'Show less' : 'Know More'}
                        <ChevronDown size={16} className={expandedItineraryDayId === day.id ? 'open' : ''} />
                      </button>
                    )}
                  </div>
                  {expandedItineraryDayId === day.id && (day.description || day.brief) && (
                    <div className="event-detail-itinerary-details">
                      <RichContent content={day.description || day.brief || day.name} />
                    </div>
                  )}
                  {day.imageUrl && (
                    <img
                      src={trekDisplayImageUrl(day.imageUrl, IMAGE_DISPLAY_WIDTH.thumb)}
                      alt={day.name}
                      className="event-detail-day-img event-detail-itinerary-img"
                    />
                  )}
                </div>
                );
              })}
            </div>
          )}

          {webstories.length > 0 && (
            <EventWebstories webstories={webstories} />
          )}

          {attractions.length > 0 && (
            <div className="event-detail-card">
              <h2 className="event-detail-section-title">Attractions And Places To Visit</h2>
              <div className="event-detail-attractions">
                {visibleAttractions.map((a) => {
                  const attractionKey = a.slug || a.attractionProfileId;
                  return (
                  <button
                    key={a.id}
                    type="button"
                    className={`event-detail-attraction-card${attractionKey ? '' : ' event-detail-attraction-card--disabled'}`}
                    title={attractionKey ? undefined : 'Add this place in Admin → Attractions and link it on the trek by name.'}
                    onClick={() => {
                      if (!attractionKey) return;
                      navigate(`/attractions/${encodeURIComponent(attractionKey)}`, {
                        state: { from: `${location.pathname}${location.search}` }
                      });
                    }}
                    aria-label={`View details: ${a.name}`}
                  >
                    <img
                      src={trekDisplayImageUrl(
                        a.imageUrl || 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=300',
                        IMAGE_DISPLAY_WIDTH.thumb
                      )}
                      alt=""
                      onError={(e) => trekImageOnError(e)}
                    />
                    <div className="event-detail-attraction-card-overlay">
                      <h4>{a.name}</h4>
                      {attractionKey && (
                        <div className="event-detail-attraction-link-icon-wrap">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="event-detail-attraction-link-icon">
                            <line x1="7" y1="17" x2="17" y2="7"/>
                            <polyline points="7 7 17 7 17 17"/>
                          </svg>
                        </div>
                      )}
                    </div>
                  </button>
                  );
                })}
              </div>
              {hasMoreAttractions && (
                <div className="event-detail-load-more-wrap">
                  <button
                    type="button"
                    className="event-detail-load-more"
                    onClick={() => setAttractionsExpanded((v) => !v)}
                    aria-expanded={attractionsExpanded}
                  >
                    {attractionsExpanded ? 'Show less' : 'Load more'}
                  </button>
                </div>
              )}
            </div>
          )}

          {(inclusions.length > 0 || exclusions.length > 0) && (
            <div className="event-detail-card">
              <div className="event-detail-inout">
                <div className="event-detail-inclusions">
                  <h4><Check size={20} /> Inclusions</h4>
                  <ul>
                    {inclusions.map((item) => {
                      const name = String(item.name || '').trim();
                      const description = String(item.description || '').trim();
                      const titleText = name || description;
                      const hasHoverDetail = Boolean(name && description);
                      const tipId = hasHoverDetail ? `inout-inc-${item.id}` : undefined;
                      return (
                        <li key={item.id}>
                          <span className="event-detail-inout-icon event-detail-inout-icon-in"><Check size={14} /></span>
                          <span
                            className={`event-detail-inout-wrap${hasHoverDetail ? ' event-detail-inout-wrap--tippable' : ''}`}
                            tabIndex={hasHoverDetail ? 0 : undefined}
                            {...(hasHoverDetail ? { 'aria-describedby': tipId } : {})}
                          >
                            <span className="event-detail-inout-label">{titleText}</span>
                            {hasHoverDetail ? (
                              <>
                                <span id={tipId} className="event-detail-sr-only">
                                  {description}
                                </span>
                                <span className="event-detail-inout-tooltip" aria-hidden="true">
                                  {description}
                                </span>
                              </>
                            ) : null}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
                <div className="event-detail-exclusions">
                  <h4><X size={20} /> Exclusions</h4>
                  <ul>
                    {exclusions.map((item) => {
                      const name = String(item.name || '').trim();
                      const description = String(item.description || '').trim();
                      const titleText = name || description;
                      const hasHoverDetail = Boolean(name && description);
                      const tipId = hasHoverDetail ? `inout-exc-${item.id}` : undefined;
                      return (
                        <li key={item.id}>
                          <span className="event-detail-inout-icon event-detail-inout-icon-ex"><X size={14} /></span>
                          <span
                            className={`event-detail-inout-wrap${hasHoverDetail ? ' event-detail-inout-wrap--tippable' : ''}`}
                            tabIndex={hasHoverDetail ? 0 : undefined}
                            {...(hasHoverDetail ? { 'aria-describedby': tipId } : {})}
                          >
                            <span className="event-detail-inout-label">{titleText}</span>
                            {hasHoverDetail ? (
                              <>
                                <span id={tipId} className="event-detail-sr-only">
                                  {description}
                                </span>
                                <span className="event-detail-inout-tooltip" aria-hidden="true">
                                  {description}
                                </span>
                              </>
                            ) : null}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {stayDetails.length > 0 && (
            <div className="event-detail-card">
              <h2 className="event-detail-section-title">Stay Details</h2>
              <div className="event-detail-stay-details-scroll">
                {stayDetails.map((stay, idx) => {
                  const stayTypeMap = {
                    hotel: { label: 'Hotel', svg: (
                      <svg viewBox="0 0 32 32" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2">
                        <rect x="2" y="27" width="28" height="3" rx="0.5"/>
                        <rect x="2" y="14" width="8" height="13"/>
                        <rect x="22" y="14" width="8" height="13"/>
                        <rect x="8" y="3" width="16" height="24"/>
                        <rect x="3.5" y="16" width="3" height="3" rx="0.5"/>
                        <rect x="3.5" y="21" width="3" height="3" rx="0.5"/>
                        <rect x="25.5" y="16" width="3" height="3" rx="0.5"/>
                        <rect x="25.5" y="21" width="3" height="3" rx="0.5"/>
                        <rect x="10" y="5.5" width="3" height="3" rx="0.5"/>
                        <rect x="14.5" y="5.5" width="3" height="3" rx="0.5"/>
                        <rect x="19" y="5.5" width="3" height="3" rx="0.5"/>
                        <rect x="10" y="11" width="3" height="3" rx="0.5"/>
                        <rect x="14.5" y="11" width="3" height="3" rx="0.5"/>
                        <rect x="19" y="11" width="3" height="3" rx="0.5"/>
                        <rect x="13" y="18.5" width="6" height="8.5"/>
                      </svg>
                    )},
                    campsite: { label: 'Campsite', svg: (
                      <svg viewBox="0 0 32 32" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2">
                        <path d="M30 7a5 5 0 1 0-5 5 3.5 3.5 0 0 1 5-5z"/>
                        <line x1="5" y1="20" x2="5" y2="28"/>
                        <line x1="3" y1="28" x2="7" y2="28"/>
                        <path d="M5 20 C2 20 0 17 1 14 C0 11 2 8 4 9 C4 7 6 6 7 8 C9 7 11 10 9 13 C11 14 10 18 8 19 C7 20 5 20 5 20Z"/>
                        <line x1="5" y1="15" x2="2" y2="18"/>
                        <line x1="5" y1="18" x2="2" y2="21"/>
                        <line x1="27" y1="20" x2="27" y2="28"/>
                        <line x1="25" y1="28" x2="29" y2="28"/>
                        <path d="M27 20 C30 20 32 17 31 14 C32 11 30 8 28 9 C28 7 26 6 25 8 C23 7 21 10 23 13 C21 14 22 18 24 19 C25 20 27 20 27 20Z"/>
                        <line x1="27" y1="15" x2="30" y2="18"/>
                        <line x1="27" y1="18" x2="30" y2="21"/>
                        <polyline points="11,28 16,14 21,28"/>
                        <line x1="16" y1="14" x2="16" y2="20"/>
                        <polyline points="13.5,28 16,22 18.5,28"/>
                      </svg>
                    )},
                    dorm: { label: 'Dorm', svg: (
                      <svg viewBox="0 0 32 32" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2">
                        <path d="M 6 4 L 6 28" />
                        <path d="M 6 13 Q 12 8 14 13" />
                        <path d="M 6 23 Q 12 18 14 23" />
                        <path d="M 6 13 L 26 13 A 2 2 0 0 1 28 15 L 28 28" />
                        <path d="M 6 23 L 28 23" />
                        <path d="M 21 13 L 21 23" />
                        <path d="M 21 18 L 28 18" />
                      </svg>
                    )},
                    tents: { label: 'Tents', svg: (
                      <svg viewBox="0 0 32 32" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2">
                        <line x1="12" y1="2" x2="18" y2="9"/>
                        <line x1="20" y1="2" x2="14" y2="9"/>
                        <line x1="16" y1="8" x2="3" y2="29"/>
                        <line x1="16" y1="8" x2="29" y2="29"/>
                        <line x1="3" y1="29" x2="29" y2="29"/>
                        <polyline points="12,29 16,21 20,29"/>
                        <line x1="3" y1="28.5" x2="1.5" y2="31"/>
                        <line x1="29" y1="28.5" x2="30.5" y2="31"/>
                      </svg>
                    )},
                    houseboats: { label: 'Houseboat', svg: (
                      <svg viewBox="0 0 32 32" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2">
                        <path d="M1 27 Q3 25 5 27 Q7 29 9 27 Q11 25 13 27 Q15 29 17 27 Q19 25 21 27 Q23 29 25 27 Q27 25 29 27 Q31 29 32 27"/>
                        <rect x="3" y="20" width="26" height="6" rx="1"/>
                        <rect x="6" y="12" width="20" height="9"/>
                        <rect x="5" y="9" width="22" height="4"/>
                        <line x1="9" y1="9" x2="9" y2="7"/>
                        <line x1="13" y1="9" x2="13" y2="7"/>
                        <line x1="17" y1="9" x2="17" y2="7"/>
                        <line x1="21" y1="9" x2="21" y2="7"/>
                        <line x1="25" y1="9" x2="25" y2="7"/>
                        <rect x="8" y="14" width="4" height="4" rx="0.5"/>
                        <rect x="14" y="14" width="4" height="4" rx="0.5"/>
                        <rect x="20" y="14" width="4" height="4" rx="0.5"/>
                        <rect x="25" y="11" width="3" height="5"/>
                        <path d="M3 20 C1 18 0 15 2 13"/>
                      </svg>
                    )},
                    homestay: { label: 'Homestay', svg: (
                      <svg viewBox="0 0 32 32" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2">
                        <line x1="0" y1="14" x2="3" y2="14"/>
                        <line x1="21" y1="14" x2="23" y2="14"/>
                        <line x1="1" y1="14" x2="13" y2="3"/>
                        <line x1="13" y1="3" x2="23" y2="12"/>
                        <rect x="3" y="14" width="18" height="14"/>
                        <path d="M10 28 L10 21 Q10 18 13 18 Q16 18 16 21 L16 28"/>
                        <polyline points="23,19 27,14 32,19"/>
                        <rect x="23" y="19" width="9" height="9"/>
                        <rect x="25" y="22" width="5" height="3" rx="0.5"/>
                      </svg>
                    )},
                  };
                  const typeInfo = stayTypeMap[stay.stayType] || null;

                  return (
                    <div key={idx} className="ed-stay-card-wrap">
                      <div className="ed-stay-card" aria-label={stay.name || 'Stay'}>
                        {stay.imageUrl ? (
                          <img src={trekDisplayImageUrl(stay.imageUrl, IMAGE_DISPLAY_WIDTH.thumb)} alt={stay.name || 'Stay'} className="ed-stay-card-img"/>
                        ) : (
                          <div className="ed-stay-card-img-placeholder" />
                        )}
                        {typeInfo && (
                          <div className="ed-stay-type-pill">
                            {typeInfo.svg}
                            <span className="ed-stay-type-label">{typeInfo.label}</span>
                          </div>
                        )}
                        {stay.durationLabel && (
                          <div className="ed-stay-duration-pill">
                            <img src="/stay-icons/night-stay.png" alt="Duration" style={{ width: '22px', height: '22px', flexShrink: 0, filter: 'invert(8%) sepia(21%) saturate(2371%) hue-rotate(185deg) brightness(97%) contrast(93%)' }} />
                            <span className="ed-stay-duration-label">{stay.durationLabel}</span>
                          </div>
                        )}
                      </div>
                      {stay.name && (
                        <p className="ed-stay-card-name">{stay.name}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}


        </div>

        <aside className="event-detail-sidebar">
          {renderPriceSidebarCard('event-detail-price-sidebar-card')}

          {shouldShowConsultantCard ? (
          <div className="event-detail-sidebar-card event-detail-consultant">
            <h4>{siteSettings?.event_consultant_card_title || 'Hey, Traveler!'}</h4>
            <p>
              {siteSettings?.event_consultant_card_subtitle ||
                'Connect With Me If You Have Any Questions Regarding This Package.'}
            </p>
            <div className="event-detail-consultant-profile">
              <div className="event-detail-consultant-profile-left">
                <div
                  className="event-detail-consultant-avatar"
                  style={
                    trekConsultant?.avatar
                      ? {
                          backgroundImage: `url(${trekDisplayImageUrl(
                            trekConsultant.avatar,
                            IMAGE_DISPLAY_WIDTH.thumb
                          )})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center'
                        }
                      : undefined
                  }
                />
                <div>
                  {consultantName ? (
                    <strong>{consultantName}</strong>
                  ) : (
                    <strong>{siteSettings?.event_consultant_fallback_team_name || 'Invincible Team'}</strong>
                  )}
                </div>
              </div>
              <a href={officeTelHref} className="event-detail-consultant-call-btn" aria-label="Call office">
                <Phone size={18} />
              </a>
            </div>
            <div className="event-detail-consultant-btns">
              <div className="event-detail-consultant-btn-row">
                <a href={officeWhatsAppHref} className="event-detail-consultant-btn" target="_blank" rel="noopener noreferrer">
                  <MessageCircle size={18} />{' '}
                  {siteSettings?.event_consultant_whatsapp_button_label || 'Whatsapp'}
                </a>
                <button type="button" className="event-detail-consultant-btn" onClick={openPdfModal}>
                  <Download size={18} /> {siteSettings?.event_consultant_pdf_button_label || 'Get PDF'}
                </button>
              </div>
              <div className="event-detail-consultant-btn-row event-detail-consultant-btn-row--callback">
                <button type="button" className="event-detail-book-now" onClick={() => setCallbackModalOpen(true)}>
                  <Phone size={18} />{' '}
                  {siteSettings?.event_consultant_callback_button_label || 'Request A Callback'}
                </button>
              </div>
            </div>
          </div>
          ) : null}

          {siteSettings?.event_special_offer_enabled === 'true' && !isLocalCampTrek(trekForSeo || trek || batch?.trek) && (
            <div className="event-detail-sidebar-card event-detail-special-11">
              <h4><Users size={20} /> {siteSettings?.event_special_offer_title || 'Invincible Special 11'}</h4>
              <p>{siteSettings?.event_special_offer_body || 'Planning A Group Adventure? Book For 10 People And Get The 11th Spot Entirely Free! Perfect For College Batches And Large Friend Groups.'}</p>
              <button type="button" className="event-detail-callback-btn" onClick={() => setCallbackModalOpen(true)}>
                <Phone size={18} />{' '}
                {siteSettings?.event_consultant_callback_button_label || 'Request A Callback'}
              </button>
            </div>
          )}
        </aside>
      </div>

      <div className="event-detail-cards-full">
          {faqs.length > 0 ? (
          <div className="event-detail-card event-detail-mobile-order-faq">
            <h2 className="event-detail-section-title">Frequently Asked Questions</h2>
            <div className="event-detail-faq-grid">
              {faqs.map((faq, i) => (
                <div
                  key={i}
                  className={`event-detail-faq-item ${expandedFaq === i ? 'open' : ''}`}
                  onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                >
                  <span>
                    <span>{faq.q}</span>
                    <ChevronDown size={18} />
                  </span>
                  {expandedFaq === i && (
                    <div className="event-detail-faq-answer">
                      <RichContent content={faq.a} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          ) : null}

          {quickPolicyLinks.length > 0 ? (
            <div className="event-detail-card event-detail-mobile-order-ql">
              <h2 className="event-detail-section-title">Quick Links & Policies</h2>
              <div className="event-detail-quick-links">
                {quickPolicyLinks.map((link, i) => {
                  const disp = getIncludeIconDisplay(link.icon);
                  const LucideIcon = getIncludeSidebarLucideComponent(disp.mode === 'emoji' ? '' : (disp.slug || link.icon));
                  return (
                    <button
                      key={`${link.title}-${i}`}
                      type="button"
                      className="event-detail-quick-link"
                      onClick={() => setPolicyModalIndex(i)}
                    >
                      {disp.mode === 'emoji' ? (
                        <span className="event-detail-quick-link-emoji" aria-hidden>{disp.text}</span>
                      ) : (
                        <LucideIcon size={22} strokeWidth={2} aria-hidden />
                      )}
                      {link.title}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          {policyModalIndex != null && quickPolicyLinks[policyModalIndex] && (() => {
            const active = quickPolicyLinks[policyModalIndex];
            const content = active.content || '';
            const trimmedPolicy = String(content || '').trim();
            const policyLooksLikeHtml = /<\/?[a-z][a-z0-9]*\b/i.test(trimmedPolicy);
            const lines = trimmedPolicy.split(/\n/).map((s) => s.trim()).filter(Boolean);
            return (
              <div className="event-detail-policy-modal-overlay" onClick={() => setPolicyModalIndex(null)} role="dialog" aria-modal="true" aria-labelledby="event-detail-policy-modal-title">
                <div className="event-detail-policy-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="event-detail-policy-modal-head">
                    <h2 id="event-detail-policy-modal-title" className="event-detail-section-title">{active.title}</h2>
                    <button type="button" className="event-detail-policy-modal-close modal-close-circle" onClick={() => setPolicyModalIndex(null)} aria-label="Close">
                      <X size={24} />
                    </button>
                  </div>
                  <div className="event-detail-policy-content event-detail-policy-modal-body">
                    {policyLooksLikeHtml ? (
                      <RichContent content={content} className="event-detail-policy-rich" />
                    ) : lines.length > 0 ? (
                      <ul className="event-detail-policy-bullets">
                        {lines.map((line, idx) => (
                          <li key={idx}>{line}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="event-detail-policy-empty">No content added yet.</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {experienceGallery.length > 0 && (
            <div className="event-detail-card event-detail-mobile-order-gallery">
              <h2 className="event-detail-section-title">Experience Gallery</h2>
              <div className="event-detail-gallery">
                {visibleExperienceGallery.map((item) =>
                  String(item.type || '').toLowerCase() === 'video' ? (
                    <button
                      key={item.id}
                      type="button"
                      className="event-detail-gallery-item event-detail-gallery-video"
                      onClick={() => setActiveVideoId(item.videoId)}
                    >
                      <img
                        src={experienceGalleryVideoPosterSrc(item)}
                        alt={item.title || 'Trek video'}
                      />
                      <div className="event-detail-gallery-video-overlay">
                        <Play size={32} />
                      </div>
                    </button>
                  ) : (
                    <div key={item.id} className="event-detail-gallery-item">
                      <img
                        src={trekDisplayImageUrl(item.url || '', IMAGE_DISPLAY_WIDTH.gallery)}
                        alt={item.title || 'Gallery'}
                        loading="lazy"
                      />
                    </div>
                  )
                )}
              </div>
              {hasMoreGallery && (
                <div className="event-detail-load-more-wrap">
                  <button
                    type="button"
                    className="event-detail-load-more"
                    onClick={() => setGalleryExpanded((v) => !v)}
                    aria-expanded={galleryExpanded}
                  >
                    {galleryExpanded ? 'Show less' : 'Load more'}
                  </button>
                </div>
              )}
            </div>
          )}

          {similarEvents.length > 0 && (
            <div className="event-detail-card event-detail-mobile-order-similar">
              <h2 className="event-detail-section-title">Similar Adventures</h2>
              <div className="event-detail-similar">
                <div className="event-detail-similar-inner">
                  {similarEvents.map((e) => (
                    <EventCard
                      key={e.id}
                      event={{
                        ...e,
                        slug: e.slug || e.id,
                        duration: formatSimilarEventDuration(e)
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
      </div>

      {/* Mobile sticky book bar */}
      <div
        className={`event-detail-mobile-bookbar ${showMobileBookBar ? '' : 'event-detail-mobile-bookbar--hidden'}`}
        role="region"
        aria-label="Book now"
      >
        <button 
          type="button"
          className="event-detail-mobile-pdf-fab-container" 
          onClick={openPdfModal} 
          aria-label="Download PDF"
          title="Download PDF"
        >
          <span className="event-detail-mobile-pdf-fab-text">Get PDF!</span>
          <div className="event-detail-mobile-pdf-fab-icon">
            <Download size={22} color="white" />
          </div>
        </button>
        <div className="event-detail-mobile-bookbar-inner">
          <div className="event-detail-mobile-bookbar-price">
            <span className="event-detail-mobile-bookbar-price-prefix">From</span>
            <span className="event-detail-mobile-bookbar-price-line2">
              <span className="event-detail-mobile-bookbar-price-value">{formatInr(displayPrice)}</span>
              <span className="event-detail-mobile-bookbar-price-suffix">/ person</span>
            </span>
          </div>

          <div className="event-detail-mobile-bookbar-actions">
            <a href={officeTelHref} className="event-detail-mobile-bookbar-call" aria-label="Call office">
              <Phone size={18} />
            </a>
            <a
              href={officeWhatsAppHref}
              className="event-detail-mobile-bookbar-whatsapp"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp"
            >
                <svg className="event-detail-mobile-bookbar-whatsapp-icon" viewBox="0 0 24 24" aria-hidden>
                  <path
                    fill="currentColor"
                    d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.472-.149-.672.149-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.672-1.611-.922-2.206-.242-.579-.487-.501-.672-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"
                  />
                </svg>
            </a>

            <button
              type="button"
              className="event-detail-mobile-bookbar-btn"
              onClick={handleBookNow}
              disabled={bookNowBlocked}
              title={bookNowTitle}
            >
              Book Now
            </button>
          </div>
        </div>
      </div>

      {callbackModalOpen && (
        <div className="event-detail-modal-overlay" onClick={() => !callbackSubmitting && setCallbackModalOpen(false)}>
          <div className="event-detail-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{siteSettings?.event_callback_modal_title || 'Request A Callback'}</h3>
            {callbackSuccess ? (
              <p className="event-detail-callback-success">Thank you! We&apos;ll get back to you soon.</p>
            ) : (
              <form onSubmit={handleCallbackSubmit}>
                <div className="event-detail-modal-field">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={callbackForm.name}
                    onChange={(e) => setCallbackForm((p) => ({ ...p, name: e.target.value }))}
                    required
                    placeholder="Your name"
                  />
                </div>
                <div className="event-detail-modal-field">
                  <label>Phone *</label>
                  <input
                    type="tel"
                    value={callbackForm.phone}
                    onChange={(e) => setCallbackForm((p) => ({ ...p, phone: normalizeMobile(e.target.value) }))}
                    required
                    placeholder="10-digit mobile"
                    inputMode="numeric"
                    maxLength={MOBILE_DIGITS}
                  />
                </div>
                <div className="event-detail-modal-field">
                  <label>Email (optional)</label>
                  <input
                    type="email"
                    value={callbackForm.email}
                    onChange={(e) => setCallbackForm((p) => ({ ...p, email: e.target.value }))}
                    placeholder="your@email.com"
                  />
                </div>
                <div className="event-detail-modal-field">
                  <label>Reason to call (optional)</label>
                  <input
                    type="text"
                    value={callbackForm.reason}
                    onChange={(e) => setCallbackForm((p) => ({ ...p, reason: e.target.value }))}
                    placeholder="e.g. itinerary, pricing, group booking"
                    maxLength={500}
                  />
                </div>
                <div className="event-detail-modal-field">
                  <label>Message (optional)</label>
                  <textarea
                    rows={3}
                    value={callbackForm.message}
                    onChange={(e) => setCallbackForm((p) => ({ ...p, message: e.target.value }))}
                    placeholder="Any question or note..."
                  />
                </div>
                <div className="event-detail-modal-actions">
                  <button type="button" className="event-detail-modal-cancel" onClick={() => setCallbackModalOpen(false)} disabled={callbackSubmitting}>
                    Cancel
                  </button>
                  <button type="submit" className="event-detail-modal-submit" disabled={callbackSubmitting}>
                    {callbackSubmitting ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
      {pdfModalOpen && (
        <div
          className="event-card-pdf-overlay"
          onClick={closePdfModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="pdf-modal-title"
        >
          <div className="event-card-pdf-modal" onClick={(e) => e.stopPropagation()}>
            <div className="event-card-pdf-modal-header">
              <h3 id="pdf-modal-title">Download Event PDF</h3>
              <button
                type="button"
                className="event-card-pdf-modal-close modal-close-circle"
                onClick={closePdfModal}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handlePdfSubmit} className="event-card-pdf-form">
              <label className="event-card-pdf-label">
                <span className="event-card-pdf-label-text">
                  Name <span className="required">*</span>
                </span>
                <input
                  type="text"
                  value={pdfForm.name}
                  onChange={(e) => setPdfForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Your name"
                  required
                />
              </label>

              <label className="event-card-pdf-label">
                <span className="event-card-pdf-label-text">
                  Mobile Number <span className="required">*</span>
                </span>
                <input
                  type="tel"
                  value={pdfForm.mobile}
                  onChange={(e) => setPdfForm((p) => ({ ...p, mobile: normalizeMobile(e.target.value) }))}
                  placeholder="10-digit mobile number"
                  required
                  inputMode="numeric"
                  maxLength={MOBILE_DIGITS}
                />
              </label>

              <label className="event-card-pdf-label">
                <span className="event-card-pdf-label-text">Event</span>
                <input
                  type="text"
                  value={batch?.trek?.name || event?.title || ''}
                  readOnly
                  disabled
                  className="event-card-pdf-event-readonly"
                />
              </label>

              {pdfError ? <p className="event-card-pdf-error">{pdfError}</p> : null}

              <button type="submit" className="event-card-pdf-submit" disabled={pdfSubmitting}>
                {pdfSubmitting ? 'Submitting...' : 'Download PDF'}
              </button>
            </form>
          </div>
        </div>
      )}
      {heroLightboxOpen && heroItems.length > 0 && (
        <div
          className="event-detail-hero-lightbox-backdrop"
          onClick={() => setHeroLightboxOpen(false)}
          role="presentation"
        >
          <div
            className="event-detail-hero-lightbox"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Trek photos"
          >
            <button
              type="button"
              className="event-detail-hero-lightbox-close modal-close-circle"
              onClick={() => setHeroLightboxOpen(false)}
              aria-label="Close gallery"
            >
              <X size={22} />
            </button>
            {heroItems.length > 1 && (
              <>
                <button
                  type="button"
                  className="event-detail-hero-lightbox-nav event-detail-hero-lightbox-prev"
                  onClick={() =>
                    setHeroLightboxIndex((i) => (i - 1 + heroItems.length) % heroItems.length)
                  }
                  aria-label="Previous image"
                >
                  <ChevronLeft size={32} strokeWidth={2} />
                </button>
                <button
                  type="button"
                  className="event-detail-hero-lightbox-nav event-detail-hero-lightbox-next"
                  onClick={() => setHeroLightboxIndex((i) => (i + 1) % heroItems.length)}
                  aria-label="Next image"
                >
                  <ChevronRight size={32} strokeWidth={2} />
                </button>
              </>
            )}
            <div className="event-detail-hero-lightbox-stage">
              <img
                src={heroItems[heroLightboxIndex]?.url}
                alt={`${trekName} — photo ${heroLightboxIndex + 1} of ${heroItems.length}`}
                onError={(e) => trekImageOnError(e, heroItems[heroLightboxIndex]?.rawUrl)}
              />
            </div>
            {heroItems.length > 1 && (
              <p className="event-detail-hero-lightbox-counter">
                {heroLightboxIndex + 1} / {heroItems.length}
              </p>
            )}
          </div>
        </div>
      )}
      {activeVideoId && (
        <div className="event-detail-video-modal-backdrop" onClick={() => setActiveVideoId(null)}>
          <div className="event-detail-video-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="event-detail-video-close modal-close-circle" onClick={() => setActiveVideoId(null)}>
              <X size={20} />
            </button>
            <div className="event-detail-video-frame">
              <iframe
                src={`https://www.youtube.com/embed/${activeVideoId}?autoplay=1`}
                title="Trek video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}

      <EventShareModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        title={trekName}
        subtitle={trekTagline}
        imageUrl={imageUrl}
        shareUrl={typeof window !== 'undefined' ? window.location.href : ''}
      />

    </div>
  );
};

export default EventDetail;
